// src/components/TableCard.js
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchPivotOptions, extractMainQuestionsPayload } from '../utils/fetchPivotOptions';
import { useVarLabelFormatter } from '../hooks/useVarLabelFormatter.js';
import VarLabelToggle from './VarLabelToggle.js';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, writeBatch, serverTimestamp, deleteDoc, query, orderBy, deleteField, limit } from 'firebase/firestore';
import { getApps, getApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { Paper, 
    Box, 
    Typography, 
    IconButton, 
    Button, 
    Backdrop, 
    CircularProgress, 
    LinearProgress ,  
    Select,
    InputLabel,
    FormControl,
    MenuItem,
    TextField,
    ListItemText,
    Checkbox,
    Stack,
    Switch,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip as MuiTooltip,
    Menu,
    ListItemIcon,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import * as XLSX from 'xlsx';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import PptxGenJS from 'pptxgenjs';
import { jsPDF } from 'jspdf';
import { BASE_COLOR_THEMES, DEFAULT_THEME_ID, getThemeById, normalizePalette } from '../constants/colorThemes.js';

// ---- Lightweight "savedTables" writer (debounced) ----

// Firestore collection name for persisted tables (meta + paged rows)
const TABLE_COLLECTION = 'tableDatasets';
const TABLE_STATE_COLLECTION = 'tableBuilderState';
const TABLE_STATE_DOC = 'pivotDefinitions';

const BASE_GROUP_ID = '__base__';
const UNGROUPED_GROUP_ID = '__ungrouped__';
const DEFAULT_BASE_GROUP_NAME = 'Base Tablolar';
const DEFAULT_OTHER_GROUP_NAME = 'Diğer Tablolar';
const SELECT_ALL_ROW_COLS = '__pivot_select_all_row_cols__';
const FIRESTORE_MAX_WRITES_BEFORE_PAUSE = 150;
const FIRESTORE_WRITE_PAUSE_MS = 40;

const DEFAULT_UNDUPE_SUFFIX = '-undupe';
const MIN_UNDUPE_MERGE_COUNT = 2;

const ROW_SERIES_PREFIX = '__rowSeries__';

// ---- New helpers: read latest server-generated tables from Firestore ----
async function readLatestReportingRun(db, projectID) {
  if (!db || !projectID) return { runId: null, meta: null, tables: [] };
  try {
    const runsRef = collection(db, 'reporting-results', projectID, 'runs');
    const qRuns = query(runsRef, orderBy('generatedAt', 'desc'), limit(1));
    const snap = await getDocs(qRuns);
    if (snap.empty) return { runId: null, meta: null, tables: [] };
    const runDoc = snap.docs[0];
    const runId = runDoc.id;
    const meta = runDoc.data() || {};

    const tablesRef = collection(db, 'reporting-results', projectID, 'runs', runId, 'tables');
    const tablesSnap = await getDocs(tablesRef);
    const tables = tablesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { runId, meta, tables };
  } catch (err) {
    console.warn('[TableCard] readLatestReportingRun error:', err);
    return { runId: null, meta: null, tables: [] };
  }
}

function normalizeColumnsForUI(cols) {
  const arr = Array.isArray(cols) ? cols : [];
  return arr.map((c) => {
    if (typeof c === 'string') {
      const header = c;
      return { field: header, headerName: header, width: Math.max(120, header.length * 10) };
    }
    const field = c.field || c.header || c.headerName || c.name;
    const headerName = c.header || c.headerName || field;
    return {
      field,
      headerName,
      width: Math.max(120, String(headerName || field || '').length * 10),
      ...c,
    };
  });
}

function normalizeRowsForUI(rows) {
  const arr = Array.isArray(rows) ? rows : [];
  return arr.map((r, i) => {
    if (r && r.id != null) return r;
    return { id: i + 1, ...r };
  });
}

function tableDocToCard(tblDoc) {
  if (!tblDoc) return null;
  const title = tblDoc.title || tblDoc.name || 'Pivot Table';
  const columns = normalizeColumnsForUI(tblDoc.columns || tblDoc.headers || []);
  const rows = normalizeRowsForUI(tblDoc.rows || []);
  if (!columns.length || !rows.length) {
    // boş tabloyu atla
    return null;
  }
  const lettersMap = tblDoc.lettersMap || (tblDoc.significance && tblDoc.significance.lettersMap) || null;
  const comparisonGroups = tblDoc.comparisonGroups || (tblDoc.significance && tblDoc.significance.comparisonGroups) || '';
  const significance = tblDoc.significance || null;

  const groupId = tblDoc.groupId ?? tblDoc.group_id ?? null;
  const groupName = tblDoc.groupName ?? tblDoc.group_name ?? null;
  const groupFilters =
    (Array.isArray(tblDoc.filtersApplied) && tblDoc.filtersApplied) ||
    (Array.isArray(tblDoc.groupFilters) && tblDoc.groupFilters) ||
    (Array.isArray(tblDoc.filters) && tblDoc.filters) ||
    [];

  return {
    id: `srv_${tblDoc.id}`,
    sourceTableId: tblDoc.tableId || tblDoc.id || null,
    title,
    columns,
    rows,
    lettersMap,
    comparisonGroups,
    significance,
    groupId: groupId || null,
    groupName: groupName || null,
    groupFilters,
    filtersApplied: groupFilters,
    isBaseGroup: Boolean(tblDoc.isBaseGroup) || groupId === '__BASE__' || groupId === '__base__',
    groupOrder: typeof tblDoc.groupOrder === 'number' ? tblDoc.groupOrder : undefined,
    generatedByQuery: true,
    querySource: 'pivotBuilder',
  };
}

const sanitizeFileName = (rawName) => {
  const fallback = 'tablecard';
  if (!rawName || typeof rawName !== 'string') return fallback;
  const trimmed = rawName.trim();
  if (!trimmed) return fallback;
  const sanitized = trimmed
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '');
  return sanitized || fallback;
};

const sanitizeSheetName = (rawName) => {
  const fallback = 'Table';
  if (!rawName || typeof rawName !== 'string') return fallback;
  const cleaned = rawName.replace(/[\[\]:\/\\?*]/g, ' ').trim();
  const sliced = cleaned.slice(0, 31);
  return sliced || fallback;
};

function TableCardToolbar({
  csvOptions,
  printOptions,
  showQuickFilter = true,
  quickFilterProps,
  onExportExcel,
  excelLabel,
  excelDisabled,
  className,
}) {
  const handleExcelClick = React.useCallback(() => {
    if (typeof onExportExcel === 'function') {
      onExportExcel();
    }
  }, [onExportExcel]);

  return (
    <GridToolbarContainer
      className={className}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        py: 1,
        px: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport csvOptions={csvOptions} printOptions={printOptions} />
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExcelClick}
          disabled={excelDisabled || typeof onExportExcel !== 'function'}
        >
          {excelLabel}
        </Button>
      </Box>
      {showQuickFilter ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 220, flexGrow: 1, maxWidth: 360 }}>
          <GridToolbarQuickFilter {...(quickFilterProps || {})} />
        </Box>
      ) : null}
    </GridToolbarContainer>
  );
}

const generateUndupeLocalId = () => `undupe_${Math.random().toString(36).slice(2, 10)}`;
const generateUndupeMergeLocalId = () => `undupeMerge_${Math.random().toString(36).slice(2, 10)}`;

function slugifyUndupeId(baseId, suffix = DEFAULT_UNDUPE_SUFFIX) {
  const raw = String(baseId || '').trim();
  const normalized = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
  const safe = normalized || 'undupe';
  const suff = String(suffix || '').trim();
  if (!suff) return safe;
  if (safe.endsWith(suff)) return safe;
  const needsSeparator = !(suff.startsWith('-') || suff.startsWith('_'));
  return `${safe}${needsSeparator ? '_' : ''}${suff}`;
}

function extractOptionsFromMeta(meta = {}) {
  if (!meta || typeof meta !== 'object') return [];
  const map = new Map();
  const addOption = (valueRaw, labelRaw) => {
    if (valueRaw == null && labelRaw == null) return;
    const valueStr = valueRaw != null && valueRaw !== '' ? String(valueRaw) : '';
    const labelStr = labelRaw != null && labelRaw !== '' ? String(labelRaw) : '';
    const key = valueStr || labelStr;
    if (!key) return;
    if (!map.has(key)) {
      map.set(key, {
        value: valueStr || key,
        label: labelStr || valueStr || key,
      });
    }
  };

  if (meta.valueLabels && typeof meta.valueLabels === 'object') {
    Object.entries(meta.valueLabels).forEach(([value, label]) => {
      addOption(value, label);
    });
  }

  if (Array.isArray(meta.optionsDetailed)) {
    meta.optionsDetailed.forEach((opt) => {
      if (!opt || typeof opt !== 'object') return;
      const val =
        opt.value ??
        opt.code ??
        opt.id ??
        opt.name ??
        opt.text ??
        opt.header ??
        '';
      const label =
        opt.label ??
        opt.text ??
        opt.name ??
        opt.header ??
        opt.value ??
        opt.code ??
        '';
      addOption(val, label);
    });
  }

  if (Array.isArray(meta.options)) {
    meta.options.forEach((opt) => {
      if (opt == null) return;
      if (typeof opt === 'string' || typeof opt === 'number') {
        const val = String(opt);
        addOption(val, opt);
        return;
      }
      if (typeof opt === 'object') {
        const val =
          opt.value ??
          opt.code ??
          opt.id ??
          opt.name ??
          opt.text ??
          opt.header ??
          '';
        const label =
          opt.label ??
          opt.text ??
          opt.name ??
          opt.header ??
          opt.value ??
          opt.code ??
          '';
        addOption(val, label);
      }
    });
  }

  return Array.from(map.values());
}

const LIKERT_CALC_OPTIONS = [
  { value: 'mean', label: 'Mean' },
  { value: 'tb', label: 'TB' },
  { value: 'tb2', label: 'TB2' },
  { value: 'tb3', label: 'TB3' },
  { value: 'mb', label: 'MB' },
  { value: 'b3b', label: 'B3B' },
  { value: 'b2b', label: 'B2B' },
  { value: 'bb', label: 'BB' },
];

const LIKERT_CALC_VALUE_SET = new Set(LIKERT_CALC_OPTIONS.map((opt) => opt.value));
const LIKERT_CALC_LABEL_MAP = new Map(LIKERT_CALC_OPTIONS.map((opt) => [opt.value, opt.label]));

const isQueryGeneratedTable = (tbl) => Boolean(tbl && (tbl.generatedByQuery || tbl.sourceTableId));

const normalizeKeyPart = (value) => {
  if (value == null) return '';
  return String(value).trim().toLowerCase();
};

const stableSerializeForKey = (value) => {
  if (value === null) return 'null';
  const type = typeof value;
  if (type === 'string') return `str:${value.trim().toLowerCase()}`;
  if (type === 'number' || type === 'boolean') return `${type}:${value}`;
  if (Array.isArray(value)) return `arr:[${value.map(stableSerializeForKey).join('|')}]`;
  if (type === 'object') {
    const keys = Object.keys(value).sort();
    return `obj:{${keys.map((key) => `${key}:${stableSerializeForKey(value[key])}`).join('|')}}`;
  }
  return `${type}:${String(value)}`;
};

const buildFiltersKey = (filters) => {
  if (!Array.isArray(filters) || !filters.length) return '';
  const serialized = filters.map((item) => stableSerializeForKey(item));
  serialized.sort();
  return serialized.join('|');
};

const getGeneratedTableSourceKey = (table) => {
  if (!table || table.sourceTableId == null) return '';
  return normalizeKeyPart(table.sourceTableId);
};

const getGeneratedTableFallbackKey = (table) => {
  if (!table) return '';
  const title = normalizeKeyPart(table.title);
  const group = normalizeKeyPart(table.groupName || table.groupLabel);
  const filtersKey = buildFiltersKey(
    Array.isArray(table.filtersApplied) && table.filtersApplied.length
      ? table.filtersApplied
      : (Array.isArray(table.groupFilters) ? table.groupFilters : [])
  );
  const parts = [];
  if (title) parts.push(`title:${title}`);
  if (group) parts.push(`group:${group}`);
  if (filtersKey) parts.push(`filters:${filtersKey}`);
  return parts.join('|');
};

const compactColumnTypesForApi = (cols = []) => {
  const safeArray = Array.isArray(cols) ? cols : [];
  return safeArray.map((col) => {
    const out = {
      header: String(col?.header || col?.id || '').trim(),
    };
    if (col?.id != null) {
      const idStr = String(col.id).trim();
      if (idStr) out.id = idStr;
    }
    const typeStr = typeof col?.type === 'string' ? col.type.trim() : '';
    const groupTypeStr = typeof col?.grouptype === 'string' ? col.grouptype.trim() : '';
    if (typeStr) out.type = typeStr;
    if (groupTypeStr) out.grouptype = groupTypeStr;
    if (typeof col?.groupname === 'string' && col.groupname.trim() !== '') {
      out.groupname = col.groupname.trim();
    }
    if (col?.manualUngroup) out.manualUngroup = true;
    if (col?.isLikert) out.isLikert = true;
    if (col?.groupLikert) out.groupLikert = true;
    if (col?.isUndupe) out.isUndupe = true;
    if (col?.sourceQuestionId != null && String(col.sourceQuestionId).trim() !== '') {
      out.sourceQuestionId = String(col.sourceQuestionId).trim();
    }
    if (col?.sourceQuestionHeader != null && String(col.sourceQuestionHeader).trim() !== '') {
      out.sourceQuestionHeader = String(col.sourceQuestionHeader).trim();
    }
    if (col?.undupe && typeof col.undupe === 'object') {
      out.undupe = col.undupe;
    }
    if (Array.isArray(col?.optionsDetailed) && col.optionsDetailed.length) {
      out.optionsDetailed = col.optionsDetailed;
    }
    if (!out.type) {
      if (out.grouptype === 'griddata' || out.grouptype === 'multigriddata') {
        out.type = out.grouptype;
      } else {
        out.type = 'multi-select data';
      }
    }
    return out;
  });
};

function extractGroupInfoFromTable(tbl = {}) {
  const explicitIdRaw = tbl.groupId ?? tbl.group_id ?? null;
  const explicitName = tbl.groupName ?? tbl.group_name ?? null;
  const filters = Array.isArray(tbl.groupFilters)
    ? tbl.groupFilters
    : (Array.isArray(tbl.filtersApplied)
        ? tbl.filtersApplied
        : (Array.isArray(tbl.filters) ? tbl.filters : []));
  const order = typeof tbl.groupOrder === 'number'
    ? tbl.groupOrder
    : (typeof tbl.group_index === 'number' ? tbl.group_index : undefined);

  let groupId = explicitIdRaw != null ? String(explicitIdRaw) : null;
  if (!groupId && explicitName) {
    groupId = `grp_${String(explicitName).toLowerCase().replace(/[^a-z0-9]+/gi, '_')}`;
  }

  const isBase = Boolean(tbl.isBaseGroup) || groupId === BASE_GROUP_ID;
  if (!groupId && isBase) {
    groupId = BASE_GROUP_ID;
  }

  if (!groupId) {
    groupId = UNGROUPED_GROUP_ID;
  }

  const name = explicitName || (groupId === BASE_GROUP_ID ? DEFAULT_BASE_GROUP_NAME : DEFAULT_OTHER_GROUP_NAME);

  return {
    id: groupId,
    name,
    filtersApplied: filters,
    isBaseGroup: isBase || groupId === BASE_GROUP_ID,
    order,
  };
}

/**
 * Tek bir tablo kartı
 */
export default function TableCard({
  title,
  rows = [],
  columns = [],
  onRename,
  onDelete,
  /* yeni props */
  collapsed = true,
  onToggle,
  meta,
  extraActions = null,
  // significance entegrasyonu
  significance = null,
  lettersMap = null,
  comparisonGroups = '',
  showSigProp = undefined,
  onToggleSigProp = null,
  colorThemes,
  defaultColorThemeId,
}) {
  // Prefer meta counts if available, fallback to loaded data
  const rowCount = meta?.rowCount ?? (Array.isArray(rows) ? rows.length : 0);
  const colCount = (meta?.columns?.length ?? (Array.isArray(columns) ? columns.length : 0));

  const { t } = useTranslation();
  const excelLabel = t('report.exportToExcel');
  const exportFileName = React.useMemo(() => sanitizeFileName(title || 'tablecard'), [title]);
  const buildFileName = React.useCallback((ext) => `${exportFileName}.${ext}`, [exportFileName]);

  const minInnerWidth = useMemo(() => {
    if (!Array.isArray(columns) || columns.length === 0) return 900;
    const sum = columns.reduce((acc, c) => acc + (c?.width ?? 150) + 8, 80);
    return Math.max(900, sum);
  }, [columns]);

  const [showSig95, setShowSig95] = React.useState(false);
  const [showSig99, setShowSig99] = React.useState(false);
  const [showPercent, setShowPercent] = React.useState(false);
  const [viewMode, setViewMode] = React.useState('table');
  const [selectedChartRows, setSelectedChartRows] = React.useState([]);
  const [selectedChartCols, setSelectedChartCols] = React.useState([]);
  const [chartTransposed, setChartTransposed] = React.useState(false);
  const [chartOrientation, setChartOrientation] = React.useState('horizontal'); // 'horizontal' => bars extend horizontally
  const themeList = React.useMemo(
    () => (Array.isArray(colorThemes) && colorThemes.length ? colorThemes : BASE_COLOR_THEMES),
    [colorThemes]
  );
  const fallbackThemeId = themeList[0]?.id || DEFAULT_THEME_ID;

  const [chartStackMode, setChartStackMode] = React.useState('stacked'); // 'stacked' | 'grouped'
  const [colorThemeId, setColorThemeId] = React.useState(() => defaultColorThemeId || fallbackThemeId);
  const colorThemeOverrideRef = React.useRef(false);

  React.useEffect(() => {
    const fallback = defaultColorThemeId || fallbackThemeId;
    if (!colorThemeOverrideRef.current && colorThemeId !== fallback) {
      setColorThemeId(fallback);
    }
    if (colorThemeOverrideRef.current && colorThemeId === fallback) {
      colorThemeOverrideRef.current = false;
    }
  }, [defaultColorThemeId, fallbackThemeId, colorThemeId]);

  const activeTheme = React.useMemo(
    () => getThemeById(colorThemeId, themeList),
    [colorThemeId, themeList]
  );
  const chartPalette = React.useMemo(() => {
    const palette = activeTheme?.palette || [];
    if (palette.length > 0) return palette;
    return normalizePalette(themeList[0]?.palette) || [];
  }, [activeTheme, themeList]);
  const chartContentRef = React.useRef(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = React.useState(null);
  const [isExporting, setIsExporting] = React.useState(false);
  const rowSelectIdRef = React.useRef(`chartRowSelect_${Math.random().toString(36).slice(2, 8)}`);
  const colSelectIdRef = React.useRef(`chartColSelect_${Math.random().toString(36).slice(2, 8)}`);
  const barsHorizontal = chartOrientation === 'horizontal';
  const chartStacked = chartStackMode === 'stacked';

  const captureChartAsCanvas = React.useCallback(async (backgroundColor) => {
    if (!chartContentRef.current) {
      throw new Error('[TableCard] Grafik içeriği henüz hazır değil');
    }
    const element = chartContentRef.current;
    const scale =
      typeof window !== 'undefined' && window.devicePixelRatio
        ? Math.max(2, window.devicePixelRatio)
        : 2;
    const canvas = await html2canvas(element, {
      backgroundColor,
      scale,
      useCORS: true,
      logging: false,
    });
    return canvas;
  }, []);

  // Backward compatibility: if parent passes a single toggle, mirror it to both when it changes
  React.useEffect(() => {
    if (typeof showSigProp === 'boolean') {
      setShowSig95(showSigProp);
      setShowSig99(showSigProp);
    }
  }, [showSigProp]);

  // Row kolon adını belirle
  const rowKeyLocal = React.useMemo(() => {
    const names = (columns || []).map(c => c.field || c.headerName).filter(Boolean);
    if (names.includes('Row')) return 'Row';
    if (names.includes('_row')) return '_row';
    const first = names.find(n => n !== 'id');
    return first || 'Row';
  }, [columns]);

  const getRowIdentifiers = React.useCallback((row, idx) => {
    const rawVal = row?.[rowKeyLocal];
    const rawStr = rawVal != null ? String(rawVal).trim() : '';
    const fallback = row?.id != null ? `Satır ${row.id}` : `Satır ${idx + 1}`;
    const display = rawStr || fallback;
    const key = row?.id != null ? String(row.id) : `row-${idx}`;
    return { key, raw: rawStr, display };
  }, [rowKeyLocal]);

  const isBaseRowLabel = React.useCallback((label) => {
    if (label == null) return false;
    const s = String(label).trim().toLowerCase();
    // Only consider participant-base style rows as Base (do NOT include 'toplam'/'total')
    // Supported: 'Base', 'Baz', 'Katılımcı', 'Katılımcılar', 'Participant(s)'
    return /^(base|baz|katılımcı(?:lar)?|participants?)\b/.test(s);
  }, []);

  const safeNum = (v) => {
    if (v === '' || v == null) return NaN;
    const n = Number(String(v).toString().replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  };

  const baseRowObj = React.useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    for (let i = rows.length - 1; i >= 0; i--) {
      const lab = rows[i]?.[rowKeyLocal];
      if (isBaseRowLabel(lab)) return rows[i];
    }
    return null;
  }, [rows, rowKeyLocal, isBaseRowLabel]);

  // lettersMap'i ters çevir: { "AB": "A", "C1": "B", ... }
  const letterByCol = React.useMemo(() => {
    const map = {};
    const lm = lettersMap || (significance && significance.lettersMap) || null;
    if (lm) Object.entries(lm).forEach(([letter, colName]) => { map[colName] = letter; });
    return map;
  }, [lettersMap, significance]);

  const sigByRow = React.useMemo(() => (significance && significance.byRow) || {}, [significance]);

  // DataGrid kolonlarını, significance ve yüzde gösterimiyle zenginleştir
  const augmentColumnsWithSig = React.useCallback((cols) => {
    if (!cols || !Array.isArray(cols)) return [];
    return cols.map(col => {
      const field = col.field || col.headerName;
      if (!field || field === 'id' || field === rowKeyLocal) return col;

      const baseRender = col.renderCell;

      const wrapWithSig = (rlabel, fieldName, rawText) => {
        const ltr = letterByCol[fieldName];
        const marks = (ltr && sigByRow[rlabel] && sigByRow[rlabel][ltr]) ? sigByRow[rlabel][ltr] : [];
        if ((!showSig95 && !showSig99) || !marks || marks.length === 0) {
          return rawText;
        }
        const filtered = (marks || []).filter(m => {
          const isUpper = typeof m === 'string' && m.length > 0 && m === m.toUpperCase() && /[A-Z]/.test(m[0]);
          const isLower = typeof m === 'string' && m.length > 0 && m === m.toLowerCase() && /[a-z]/.test(m[0]);
          return (showSig95 && isUpper) || (showSig99 && isLower);
        });
        if (!filtered.length) return rawText;
        return (
          <span style={{ display:'inline-block', padding:'2px 4px', backgroundColor:'rgba(255,0,0,0.08)', borderRadius:3 }}>
            {rawText} <span style={{ color:'red', fontWeight:700 }}>({filtered.join('')})</span>
          </span>
        );
      };

      return {
        ...col,
        renderHeader: (params) => {
          const baseTitle = col.headerName || col.field || '';
          const ltrHdr = letterByCol[field];
          const showAnySig = showSig95 || showSig99;
          return (
            <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
              <span>{baseTitle}</span>
              {showAnySig && ltrHdr ? <span style={{ color:'red', fontWeight:700 }}>[{ltrHdr}]</span> : null}
              {showPercent ? <span style={{ fontSize:12, opacity:.8 }}>%</span> : null}
            </span>
          );
        },
        renderCell: (params) => {
          const rlabel = String(params.row?.[rowKeyLocal] ?? '');
          const rawVal = params.value;
          let displayNode;

          if (showPercent && baseRowObj && !isBaseRowLabel(rlabel)) {
            const baseVal = safeNum(baseRowObj[field]);
            const cellVal = safeNum(rawVal);
            if (Number.isFinite(baseVal) && baseVal !== 0 && Number.isFinite(cellVal)) {
              const pct = (cellVal / baseVal) * 100;
              displayNode = `${pct.toFixed(1)}%`;
            } else {
              // fallback to original rendering if not computable
              displayNode = baseRender ? baseRender(params) : (rawVal ?? '');
            }
          } else {
            displayNode = baseRender ? baseRender(params) : (rawVal ?? '');
          }

          return wrapWithSig(rlabel, field, displayNode);
        }
      };
    });
  }, [showSig95, showSig99, sigByRow, letterByCol, rowKeyLocal, showPercent, baseRowObj, isBaseRowLabel]);

  const augColumns = React.useMemo(() => augmentColumnsWithSig(columns), [columns, augmentColumnsWithSig]);

  const columnLabelByField = React.useMemo(() => {
    const map = {};
    (Array.isArray(columns) ? columns : []).forEach((col) => {
      const field = col?.field || col?.headerName;
      if (!field || field === 'id' || field === rowKeyLocal) return;
      const label = col?.headerName || col?.field || field;
      map[field] = label;
    });
    return map;
  }, [columns, rowKeyLocal]);

  const isBaseColumn = React.useCallback(
    (field) => {
      if (!field) return false;
      const label = columnLabelByField[field] || field;
      const normalized = String(label).trim().toLowerCase();
      return normalized === 'base' || normalized.startsWith('base ');
    },
    [columnLabelByField]
  );

  const baseColumnField = React.useMemo(() => {
    if (!Array.isArray(columns)) return null;
    for (const col of columns) {
      const field = col?.field || col?.headerName;
      if (!field || field === 'id' || field === rowKeyLocal) continue;
      if (isBaseColumn(field)) return field;
    }
    return null;
  }, [columns, isBaseColumn, rowKeyLocal]);

  const exportableColumns = React.useMemo(() => {
    if (!Array.isArray(columns)) return [];
    return columns.filter((col) => {
      if (!col || typeof col !== 'object') return false;
      if (col.hide) return false;
      const field = col.field || col.headerName;
      if (!field) return false;
      return field !== 'id';
    });
  }, [columns]);

  const handleExportExcel = React.useCallback(() => {
    if (!exportableColumns.length) {
      console.warn('[TableCard] Excel export aborted: no visible columns to export.');
      return;
    }

    const headerRow = exportableColumns.map((col) => {
      const field = col?.field || col?.headerName || '';
      const baseTitle = col?.headerName || columnLabelByField[field] || field || '';
      const ltr = letterByCol[field];
      const showAnySig = showSig95 || showSig99;
      let header = baseTitle;
      if (showAnySig && ltr) {
        header = `${header} [${ltr}]`;
      }
      if (showPercent) {
        header = `${header} %`;
      }
      return header;
    });

    const dataRows = (Array.isArray(rows) ? rows : []).map((row) =>
      exportableColumns.map((col) => {
        const field = col?.field || col?.headerName;
        if (!field) return '';
        const rlabelRaw = row?.[rowKeyLocal];
        const rlabel = rlabelRaw != null ? String(rlabelRaw) : '';
        const percentApplicable =
          showPercent &&
          baseRowObj &&
          !isBaseRowLabel(rlabelRaw);

        let displayValue;
        if (percentApplicable) {
          const baseVal = safeNum(baseRowObj[field]);
          const cellVal = safeNum(row?.[field]);
          if (Number.isFinite(baseVal) && baseVal !== 0 && Number.isFinite(cellVal)) {
            const pct = (cellVal / baseVal) * 100;
            displayValue = `${pct.toFixed(1)}%`;
          }
        }

        if (displayValue == null) {
          const value = row?.[field];
          if (value == null) {
            displayValue = '';
          } else if (typeof value === 'number' && Number.isFinite(value)) {
            displayValue = value;
          } else if (typeof value === 'boolean') {
            displayValue = value ? 'TRUE' : 'FALSE';
          } else if (value instanceof Date) {
            displayValue = value;
          } else if (typeof value === 'object') {
            try {
              displayValue = JSON.stringify(value);
            } catch (err) {
              console.warn('[TableCard] Excel export: failed to stringify cell value', err);
              displayValue = '';
            }
          } else {
            displayValue = value;
          }
        }

        if ((showSig95 || showSig99) && letterByCol[field]) {
          const letter = letterByCol[field];
          const marks = sigByRow[rlabel]?.[letter] || [];
          const filtered = marks.filter((m) => {
            const isUpper =
              typeof m === 'string' && m.length > 0 && m === m.toUpperCase() && /[A-Z]/.test(m[0]);
            const isLower =
              typeof m === 'string' && m.length > 0 && m === m.toLowerCase() && /[a-z]/.test(m[0]);
            return (showSig95 && isUpper) || (showSig99 && isLower);
          });
          if (filtered.length) {
            const marksStr = `(${filtered.join('')})`;
            if (displayValue == null || displayValue === '') {
              displayValue = marksStr;
            } else if (displayValue instanceof Date) {
              displayValue = `${displayValue.toISOString()} ${marksStr}`;
            } else if (typeof displayValue === 'number') {
              displayValue = `${displayValue} ${marksStr}`;
            } else {
              displayValue = `${displayValue} ${marksStr}`;
            }
          }
        }

        return displayValue;
      })
    );

    const worksheetData = [headerRow, ...dataRows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    if (worksheet && exportableColumns.length > 0) {
      worksheet['!cols'] = headerRow.map((header, colIdx) => {
        const headerLength = header ? String(header).length : 0;
        const maxCellLength = dataRows.reduce((max, row) => {
          const cell = row[colIdx];
          const cellLength = cell == null ? 0 : String(cell).length;
          return Math.max(max, cellLength);
        }, 0);
        const width = Math.min(Math.max(Math.max(headerLength, maxCellLength) + 2, 10), 60);
        return { wch: width };
      });
    }

    const workbook = XLSX.utils.book_new();
    const sheetName = sanitizeSheetName(exportFileName);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    try {
      XLSX.writeFile(workbook, `${exportFileName}.xlsx`, { compression: true });
    } catch (err) {
      console.error('[TableCard] Excel export failed', err);
    }
  }, [
    exportableColumns,
    rows,
    columnLabelByField,
    exportFileName,
    letterByCol,
    sigByRow,
    showSig95,
    showSig99,
    showPercent,
    baseRowObj,
    isBaseRowLabel,
    rowKeyLocal,
  ]);

  const excludedRowLabels = React.useMemo(
    () => new Set(['mean', 'tb', 't2b', 't3b', 'mb', 'b2b', 'b3b', 'bb']),
    []
  );

  const isDefaultExcludedRow = React.useCallback(
    (label) => {
      if (label == null) return false;
      const normalized = String(label).trim().toLowerCase();
      const squeezed = normalized.replace(/\s+/g, '');
      return excludedRowLabels.has(normalized) || excludedRowLabels.has(squeezed);
    },
    [excludedRowLabels]
  );

  const valueFields = React.useMemo(() => {
    if (!Array.isArray(columns) || columns.length === 0) return [];
    const unique = [];
    const pushUnique = (field) => {
      if (!field || field === 'id' || field === rowKeyLocal) return;
      if (!unique.includes(field)) unique.push(field);
    };
    columns.forEach((col) => {
      const field = col?.field || col?.headerName;
      pushUnique(field);
    });
    if (!Array.isArray(rows) || rows.length === 0) return unique;
    return unique.filter((field) =>
      rows.some((row) => Number.isFinite(safeNum(row?.[field])))
    );
  }, [columns, rowKeyLocal, rows]);

  const chartRowOptions = React.useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const seen = new Set();
    const options = [];
    rows.forEach((row, idx) => {
      const { key, raw, display } = getRowIdentifiers(row, idx);
      if (!key || isBaseRowLabel(raw) || seen.has(key)) return;
      seen.add(key);
      options.push({ value: key, label: display });
    });
    return options;
  }, [rows, getRowIdentifiers, isBaseRowLabel]);

  const chartRowLabelMap = React.useMemo(() => {
    const map = new Map();
    chartRowOptions.forEach((opt) => {
      map.set(opt.value, opt.label);
    });
    return map;
  }, [chartRowOptions]);

  const chartColumnOptions = React.useMemo(() => {
    if (!Array.isArray(valueFields) || valueFields.length === 0) return [];
    return valueFields.map((field) => ({
      value: field,
      label: columnLabelByField[field] || field,
    }));
  }, [valueFields, columnLabelByField]);

  const chartColumnLabelMap = React.useMemo(() => {
    const map = new Map();
    chartColumnOptions.forEach((opt) => {
      map.set(opt.value, opt.label);
    });
    return map;
  }, [chartColumnOptions]);

  const compactSelectMenuProps = React.useMemo(
    () => ({
      PaperProps: {
        sx: {
          maxHeight: 280,
          width: 260,
          '& .MuiMenuItem-root': {
            minHeight: 32,
            py: 0.5,
            gap: 0.75,
          },
          '& .MuiCheckbox-root': {
            mr: 1,
            p: 0.25,
          },
        },
      },
    }),
    []
  );

  React.useEffect(() => {
    const valid = chartRowOptions.map((opt) => opt.value);
    if (valid.length === 0) {
      setSelectedChartRows([]);
      return;
    }
    setSelectedChartRows((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const filtered = safePrev.filter((val) => valid.includes(val));
      if (filtered.length > 0) {
        const unchanged =
          filtered.length === safePrev.length &&
          filtered.every((val, idx) => val === safePrev[idx]);
        return unchanged ? prev : filtered;
      }
      const defaults = chartRowOptions
        .filter((opt) => !isDefaultExcludedRow(opt.label))
        .map((opt) => opt.value);
      if (defaults.length > 0) return defaults;
      return valid;
    });
  }, [chartRowOptions, isDefaultExcludedRow]);

  React.useEffect(() => {
    const valid = chartColumnOptions.map((opt) => opt.value);
    if (valid.length === 0) {
      setSelectedChartCols([]);
      return;
    }
    setSelectedChartCols((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const filtered = safePrev.filter((val) => valid.includes(val));
      if (filtered.length > 0) {
        const unchanged =
          filtered.length === safePrev.length &&
          filtered.every((val, idx) => val === safePrev[idx]);
        return unchanged ? prev : filtered;
      }
      const defaults = valid.filter((field) => !isBaseColumn(field));
      if (defaults.length > 0) return defaults;
      return valid;
    });
  }, [chartColumnOptions, isBaseColumn]);

  const parseChartValue = React.useCallback((val) => {
    if (typeof val === 'string') {
      const normalized = val
        .trim()
        .replace(/%/g, '')
        .replace(/\s+/g, '')
        .replace(',', '.');
      const parsed = Number(normalized);
      if (Number.isFinite(parsed)) return parsed;
    }
    return safeNum(val);
  }, []);

  const getPercentDenominator = React.useCallback(
    (row, field, preferColumnBase = true) => {
      if (!showPercent) return null;
      const baseFromColumn = baseColumnField ? parseChartValue(row?.[baseColumnField]) : NaN;
      const baseFromRow = baseRowObj ? parseChartValue(baseRowObj[field]) : NaN;
      if (preferColumnBase) {
        if (Number.isFinite(baseFromColumn) && baseFromColumn !== 0) return baseFromColumn;
        if (Number.isFinite(baseFromRow) && baseFromRow !== 0) return baseFromRow;
      } else {
        if (Number.isFinite(baseFromRow) && baseFromRow !== 0) return baseFromRow;
        if (Number.isFinite(baseFromColumn) && baseFromColumn !== 0) return baseFromColumn;
      }
      return null;
    },
    [showPercent, baseColumnField, parseChartValue, baseRowObj]
  );

  const activeChartRowKeys = React.useMemo(() => {
    if (!Array.isArray(selectedChartRows)) return [];
    const validSet = new Set(chartRowOptions.map((opt) => opt.value));
    return selectedChartRows.filter((val) => validSet.has(val));
  }, [selectedChartRows, chartRowOptions]);

  const activeChartColumns = React.useMemo(() => {
    if (!Array.isArray(selectedChartCols)) return [];
    const validSet = new Set(chartColumnOptions.map((opt) => opt.value));
    return selectedChartCols.filter((val) => validSet.has(val));
  }, [selectedChartCols, chartColumnOptions]);

  const chartConfig = React.useMemo(() => {
    const emptyConfig = {
      data: [],
      seriesKeys: [],
      seriesColorMap: {},
      seriesNameMap: {},
    };

    if (!Array.isArray(rows) || rows.length === 0) return emptyConfig;
    if (!Array.isArray(activeChartColumns) || activeChartColumns.length === 0) return emptyConfig;
    if (!Array.isArray(activeChartRowKeys) || activeChartRowKeys.length === 0) return emptyConfig;

    const activeRowSet = new Set(activeChartRowKeys);
    const activeRowsList = [];

    rows.forEach((row, idx) => {
      if (!row) return;
      const { key, raw, display } = getRowIdentifiers(row, idx);
      if (!activeRowSet.has(key) || isBaseRowLabel(raw)) return;
      activeRowsList.push({ key, raw, display, row });
    });

    if (activeRowsList.length === 0) return emptyConfig;

    if (!chartTransposed) {
      const data = activeRowsList
        .map(({ row, display }) => {
          const entry = { name: display };
          let hasAny = false;

          activeChartColumns.forEach((field) => {
            const cellVal = parseChartValue(row[field]);
            let value = Number.isFinite(cellVal) ? cellVal : NaN;
            if (showPercent) {
              const denom = getPercentDenominator(row, field, true);
              if (Number.isFinite(denom) && denom !== 0 && Number.isFinite(cellVal)) {
                value = (cellVal / denom) * 100;
              }
            }
            const safeValue = Number.isFinite(value) ? value : 0;
            entry[field] = safeValue;
            if (safeValue !== 0) {
              hasAny = true;
            }
          });

          return hasAny ? entry : null;
        })
        .filter(Boolean);

      const seriesColorMap = {};
      const seriesNameMap = {};

      activeChartColumns.forEach((field, idx) => {
        seriesColorMap[field] = chartPalette[idx % chartPalette.length];
        seriesNameMap[field] = chartColumnLabelMap.get(field) || field;
      });

      return {
        data,
        seriesKeys: activeChartColumns,
        seriesColorMap,
        seriesNameMap,
      };
    }

    const rowSeriesList = activeRowsList.map((rowInfo, idx) => ({
      ...rowInfo,
      seriesKey: `${ROW_SERIES_PREFIX}${idx}`,
    }));

    const seriesColorMap = {};
    const seriesNameMap = {};

    rowSeriesList.forEach(({ seriesKey, display }, idx) => {
      seriesColorMap[seriesKey] = chartPalette[idx % chartPalette.length];
      seriesNameMap[seriesKey] = display;
    });

    const data = activeChartColumns
      .map((field) => {
        const entry = {
          name: chartColumnLabelMap.get(field) || field,
        };
        let hasAny = false;

        rowSeriesList.forEach(({ seriesKey, row }) => {
          const cellVal = parseChartValue(row[field]);
          let value = Number.isFinite(cellVal) ? cellVal : NaN;
          if (showPercent) {
            const denom = getPercentDenominator(row, field, false);
            if (Number.isFinite(denom) && denom !== 0 && Number.isFinite(cellVal)) {
              value = (cellVal / denom) * 100;
            }
          }
          const safeValue = Number.isFinite(value) ? value : 0;
          entry[seriesKey] = safeValue;
          if (safeValue !== 0) {
            hasAny = true;
          }
        });

        return hasAny ? entry : null;
      })
      .filter(Boolean);

    return {
      data,
      seriesKeys: rowSeriesList.map(({ seriesKey }) => seriesKey),
      seriesColorMap,
      seriesNameMap,
    };
  }, [
    rows,
    activeChartColumns,
    activeChartRowKeys,
    getRowIdentifiers,
    isBaseRowLabel,
    parseChartValue,
    showPercent,
    baseRowObj,
    chartColumnLabelMap,
    chartTransposed,
    getPercentDenominator,
    baseColumnField,
    chartPalette,
  ]);

  const chartData = chartConfig.data;
  const chartSeriesKeys = chartConfig.seriesKeys;
  const chartSeriesColorMap = chartConfig.seriesColorMap;
  const chartSeriesNameMap = chartConfig.seriesNameMap;

  const exportAsPptx = React.useCallback(async () => {
    const pptx = new PptxGenJS();
    pptx.defineLayout({ name: 'CUSTOM_WIDE', width: 13.33, height: 7.5 });
    pptx.layout = 'CUSTOM_WIDE';

    const slide = pptx.addSlide();
    const titleText = title ? String(title) : 'Grafik';
    slide.addText(titleText, {
      x: 0.5,
      y: 0.4,
      w: 12.3,
      h: 0.6,
      fontSize: 22,
      bold: true,
      color: '2F3C4C',
    });

    const hasSeries =
      viewMode === 'chart' &&
      Array.isArray(chartData) &&
      chartData.length > 0 &&
      Array.isArray(chartSeriesKeys) &&
      chartSeriesKeys.length > 0;

    if (hasSeries) {
      const labels = chartData.map((row) => String(row?.name ?? ''));
      const chartSeries = chartSeriesKeys.map((seriesKey) => ({
        name: chartSeriesNameMap[seriesKey] || seriesKey,
        labels,
        values: chartData.map((row) => Number(row?.[seriesKey]) || 0),
      }));

      const chartOptions = {
        x: 0.7,
        y: 1,
        w: 11.5,
        h: 5.6,
        barDir: barsHorizontal ? 'bar' : 'col',
        showLegend: true,
        dataLabelPosition: 'none',
        dataLabelFormatCode: showPercent ? '0.0%' : undefined,
        barGrouping: chartStacked ? 'stacked' : 'clustered',
      };

      if (Array.isArray(chartPalette) && chartPalette.length > 0) {
        chartOptions.dataColors = chartPalette;
      }

      slide.addChart('bar', chartSeries, chartOptions);
    } else {
      const canvas = await captureChartAsCanvas('#ffffff');
      const dataUrl = canvas.toDataURL('image/png', 1);
      const maxWidth = 11.5;
      const maxHeight = 5.6;
      let imgWidth = maxWidth;
      let imgHeight = (canvas.height / canvas.width) * imgWidth;
      if (imgHeight > maxHeight) {
        const scale = maxHeight / imgHeight;
        imgHeight = maxHeight;
        imgWidth *= scale;
      }
      slide.addImage({
        data: dataUrl,
        x: 0.7 + (maxWidth - imgWidth) / 2,
        y: 1 + (maxHeight - imgHeight) / 2,
        w: imgWidth,
        h: imgHeight,
      });
    }

    await pptx.writeFile({ fileName: buildFileName('pptx') });
  }, [
    barsHorizontal,
    buildFileName,
    captureChartAsCanvas,
    chartData,
    chartPalette,
    chartSeriesKeys,
    chartSeriesNameMap,
    showPercent,
    title,
    viewMode,
    chartStacked,
  ]);

  const handleCloseDownloadMenu = React.useCallback(() => {
    setDownloadAnchorEl(null);
  }, []);

  const handleDownloadRequest = React.useCallback(async (format) => {
    handleCloseDownloadMenu();
    if (viewMode !== 'chart') return;
    if (!chartContentRef.current) return;
    if (!Array.isArray(chartData) || chartData.length === 0) return;
    if (!Array.isArray(chartSeriesKeys) || chartSeriesKeys.length === 0) return;

    setIsExporting(true);
    try {
      if (format === 'pptx') {
        await exportAsPptx();
        return;
      }

      const canvas = await captureChartAsCanvas(format === 'png' ? null : '#ffffff');

      if (format === 'png' || format === 'jpeg') {
        const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
        const dataUrl = canvas.toDataURL(mimeType, 1);
        const blob = await (await fetch(dataUrl)).blob();
        saveAs(blob, buildFileName(format));
        return;
      }

      if (format === 'pdf') {
        const dataUrl = canvas.toDataURL('image/jpeg', 1);
        const orientationPdf = canvas.width >= canvas.height ? 'l' : 'p';
        const pdf = new jsPDF({
          orientation: orientationPdf,
          unit: 'px',
          format: [canvas.width, canvas.height],
        });
        pdf.addImage(dataUrl, 'JPEG', 0, 0, canvas.width, canvas.height);
        pdf.save(buildFileName('pdf'));
      }
    } catch (err) {
      console.error('[TableCard] Grafik dışa aktarılırken hata oluştu', err);
    } finally {
      setIsExporting(false);
    }
  }, [
    buildFileName,
    captureChartAsCanvas,
    chartData,
    chartSeriesKeys,
    exportAsPptx,
    handleCloseDownloadMenu,
    viewMode,
  ]);

  const handleOpenDownloadMenu = React.useCallback((event) => {
    if (isExporting) return;
    if (viewMode !== 'chart') return;
    if (!Array.isArray(chartData) || chartData.length === 0) return;
    if (!Array.isArray(chartSeriesKeys) || chartSeriesKeys.length === 0) return;
    setDownloadAnchorEl(event.currentTarget);
  }, [chartData, chartSeriesKeys, isExporting, viewMode]);

  const canExportChart =
    viewMode === 'chart' &&
    Array.isArray(chartData) &&
    chartData.length > 0 &&
    Array.isArray(chartSeriesKeys) &&
    chartSeriesKeys.length > 0;

  const chartValueDomain = React.useMemo(() => {
    if (!showPercent) return [0, 'auto'];
    if (
      !Array.isArray(chartData) ||
      chartData.length === 0 ||
      !Array.isArray(chartSeriesKeys) ||
      chartSeriesKeys.length === 0
    ) {
      return [0, 100];
    }

    let maxPositiveStack = 0;
    let minNegativeStack = 0;

    chartData.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      let posSum = 0;
      let negSum = 0;

      chartSeriesKeys.forEach((seriesKey) => {
        const raw = entry[seriesKey];
        const val = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isFinite(val)) return;
        if (val >= 0) posSum += val;
        else negSum += val;
      });

      if (posSum > maxPositiveStack) maxPositiveStack = posSum;
      if (negSum < minNegativeStack) minNegativeStack = negSum;
    });

    let upperBound;
    if (maxPositiveStack <= 0) {
      upperBound = 0;
    } else if (maxPositiveStack <= 100) {
      upperBound = 100;
    } else {
      upperBound = Number((maxPositiveStack * 1.1).toFixed(1));
    }

    let lowerBound;
    if (minNegativeStack >= 0) {
      lowerBound = 0;
    } else {
      const magnitude = Math.abs(minNegativeStack);
      if (magnitude <= 100) {
        lowerBound = -100;
      } else {
        lowerBound = Number((minNegativeStack * 1.1).toFixed(1));
      }
    }

    if (!Number.isFinite(upperBound) || !Number.isFinite(lowerBound)) {
      return [0, 100];
    }

    if (lowerBound >= upperBound) {
      if (upperBound <= 0) {
        upperBound = 100;
        lowerBound = minNegativeStack < 0 ? lowerBound : 0;
      } else {
        lowerBound = Math.min(0, lowerBound);
      }
    }

    return [lowerBound, upperBound];
  }, [showPercent, chartData, chartSeriesKeys]);

  const chartLayout = barsHorizontal ? 'vertical' : 'horizontal';
  const chartMargins = barsHorizontal
    ? { top: 16, right: 24, bottom: 16, left: 0 }
    : { top: 16, right: 24, bottom: 32, left: 24 };

  const formatChartValue = React.useCallback(
    (value) => {
      if (!Number.isFinite(value)) return '−';
      if (showPercent) return `${value.toFixed(1)}%`;
      return value.toLocaleString('tr-TR');
    },
    [showPercent]
  );

  const tooltipFormatter = React.useCallback(
    (value, name) => [formatChartValue(value), name],
    [formatChartValue]
  );

  const chartHeight = React.useMemo(
    () => Math.max(320, (chartData?.length || 0) * 56),
    [chartData]
  );

  const cardSurfaceSx = {
    borderRadius: 4,
    px: { xs: 2, md: 3 },
    py: { xs: 2, md: 2.5 },
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    border: '1px solid rgba(148, 163, 184, 0.16)',
    boxShadow: '0 18px 32px rgba(148, 163, 184, 0.2)',
    backgroundColor: '#ffffff',
  };

  const pillButtonBaseSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 999,
    px: 1.75,
  };

  const primaryButtonSx = {
    ...pillButtonBaseSx,
    background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
    color: '#0f172a',
    boxShadow: '0 12px 24px rgba(99, 102, 241, 0.3)',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    '&:hover': {
      background: 'linear-gradient(135deg, #9333ea 0%, #4f46e5 100%)',
      boxShadow: '0 16px 28px rgba(99, 102, 241, 0.34)',
    },
  };

  const secondaryButtonSx = {
    ...pillButtonBaseSx,
    backgroundColor: '#ffffff',
    border: '1px solid rgba(148, 163, 184, 0.3)',
    color: '#1f2937',
    boxShadow: '0 10px 20px rgba(148, 163, 184, 0.18)',
    '&:hover': {
      backgroundColor: '#e0e7ff',
    },
  };

  const inlineControlSx = {
    px: 1.25,
    py: 0.75,
    borderRadius: 2,
    border: '1px solid rgba(148, 163, 184, 0.16)',
    backgroundColor: 'rgba(248, 250, 252, 0.85)',
  };

  const iconButtonSx = {
    borderRadius: 2,
    border: '1px solid rgba(148, 163, 184, 0.2)',
    backgroundColor: 'rgba(248, 250, 252, 0.9)',
    '&:hover': {
      backgroundColor: '#e0e7ff',
    },
  };

  const showSigBlock = significance && significance.byRow && Object.keys(significance.byRow).length > 0;

  // --- PIVOT ÇALIŞTIR VE TABLO EKLE fonksiyonu moved to TableCardPanel ---
  return (
    <Paper sx={cardSurfaceSx}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography variant="h6" sx={{ color: '#0f172a' }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {(meta?.sheet ? `${meta.sheet} • ` : '')}{rowCount} satır • {colCount} kolon
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          rowGap={0.75}
          justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        >
          {showSigBlock && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ ...inlineControlSx, flexWrap: 'wrap' }}
              useFlexGap
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                Anlamlılık
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Checkbox
                  size="small"
                  checked={showSig95}
                  onChange={(e) => {
                    setShowSig95(e.target.checked);
                    onToggleSigProp && onToggleSigProp(e.target.checked && showSig99);
                  }}
                />
                <Typography variant="caption">%95</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Checkbox
                  size="small"
                  checked={showSig99}
                  onChange={(e) => {
                    setShowSig99(e.target.checked);
                    onToggleSigProp && onToggleSigProp(e.target.checked && showSig95);
                  }}
                />
                <Typography variant="caption">%90</Typography>
              </Stack>
            </Stack>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={inlineControlSx}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
              Görünüm
            </Typography>
            <Button
              size="small"
              variant={viewMode === 'table' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('table')}
              sx={{
                textTransform: 'none',
                minWidth: 72,
                px: 1.5,
              }}
            >
              Tablo
            </Button>
            <Button
              size="small"
              variant={viewMode === 'chart' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('chart')}
              sx={{
                textTransform: 'none',
                minWidth: 72,
                px: 1.5,
              }}
            >
              Grafik
            </Button>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={inlineControlSx}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
              Frekans / %
            </Typography>
            <Switch
              size="small"
              checked={showPercent}
              onChange={(e) => setShowPercent(e.target.checked)}
            />
            <Typography variant="caption">%</Typography>
          </Stack>

          <Button
            size="small"
            variant="contained"
            onClick={onToggle}
            sx={primaryButtonSx}
          >
            {collapsed ? 'Göster' : 'Gizle'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon fontSize="small" />}
            onClick={onRename}
            sx={secondaryButtonSx}
          >
            Yeniden Adlandır
          </Button>
          {extraActions ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {extraActions}
            </Box>
          ) : null}
          <IconButton size="small" onClick={onDelete} sx={iconButtonSx}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {collapsed ? (
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.25, md: 1.5 },
            borderRadius: 2,
            border: '1px dashed rgba(148, 163, 184, 0.35)',
            backgroundColor: 'rgba(248, 250, 252, 0.85)',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Özet görünüm — tablo gizli (performans için render edilmedi).{' '}
            {(!rows || rows.length === 0) && rowCount > 0
              ? 'Veri sol listeden tablo seçildiğinde yüklenecek.'
              : ''}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            borderRadius: 2,
            border: '1px solid rgba(148, 163, 184, 0.16)',
            backgroundColor: '#F8FAFC',
            boxShadow: '0 12px 20px rgba(148, 163, 184, 0.14)',
            px: { xs: 1.25, md: 1.75 },
            py: { xs: 1.25, md: 1.5 },
            overflowX: 'auto',
          }}
        >
          {viewMode === 'chart' && (
            <Box sx={{ mb: 2 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <FormControl
                  size="small"
                  sx={{ width: { xs: '100%', md: 220 }, maxWidth: 260 }}
                >
                  <InputLabel id={`${rowSelectIdRef.current}-label`}>Satırlar</InputLabel>
                  <Select
                    labelId={`${rowSelectIdRef.current}-label`}
                    id={rowSelectIdRef.current}
                    multiple
                    label="Satırlar"
                    value={selectedChartRows}
                    onChange={(event) => {
                      const { value } = event.target;
                      setSelectedChartRows(
                        typeof value === 'string' ? value.split(',') : value
                      );
                    }}
                    renderValue={(selected) => {
                      if (!Array.isArray(selected) || selected.length === 0) {
                        return 'Seçim yok';
                      }
                      const labels = selected
                        .map((val) => chartRowLabelMap.get(val) || val)
                        .filter(Boolean);
                      if (labels.length === 1) return labels[0];
                      const compact = labels.slice(0, 2).join(', ');
                      const remaining = labels.length - 2;
                      return remaining > 0 ? `${compact} +${remaining}` : compact;
                    }}
                    MenuProps={compactSelectMenuProps}
                    sx={{
                      '& .MuiSelect-select': {
                        py: 0.65,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    {chartRowOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox
                          size="small"
                          checked={selectedChartRows.indexOf(opt.value) > -1}
                        />
                        <ListItemText
                          primary={opt.label}
                          primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ width: { xs: '100%', md: 220 }, maxWidth: 260 }}
                >
                  <InputLabel id={`${colSelectIdRef.current}-label`}>Kolonlar</InputLabel>
                  <Select
                    labelId={`${colSelectIdRef.current}-label`}
                    id={colSelectIdRef.current}
                    multiple
                    label="Kolonlar"
                    value={selectedChartCols}
                    onChange={(event) => {
                      const { value } = event.target;
                      setSelectedChartCols(
                        typeof value === 'string' ? value.split(',') : value
                      );
                    }}
                    renderValue={(selected) => {
                      if (!Array.isArray(selected) || selected.length === 0) {
                        return 'Seçim yok';
                      }
                      const labels = selected
                        .map((val) => chartColumnLabelMap.get(val) || val)
                        .filter(Boolean);
                      if (labels.length === 1) return labels[0];
                      const compact = labels.slice(0, 2).join(', ');
                      const remaining = labels.length - 2;
                      return remaining > 0 ? `${compact} +${remaining}` : compact;
                    }}
                    MenuProps={compactSelectMenuProps}
                    sx={{
                      '& .MuiSelect-select': {
                        py: 0.65,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    {chartColumnOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox
                          size="small"
                          checked={selectedChartCols.indexOf(opt.value) > -1}
                        />
                        <ListItemText
                          primary={opt.label}
                          primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box
                  sx={{
                    ...inlineControlSx,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    minHeight: 40,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                    Ekseni Çevir
                  </Typography>
                  <Switch
                    size="small"
                    checked={chartTransposed}
                    onChange={(event) => setChartTransposed(event.target.checked)}
                  />
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ ...inlineControlSx, minHeight: 40 }}
                >
                  <MuiTooltip title="Grafiği Dışa Aktar">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleOpenDownloadMenu}
                        disabled={!canExportChart || isExporting}
                        sx={{ color: '#475569' }}
                      >
                        {isExporting ? (
                          <CircularProgress size={16} thickness={5} />
                        ) : (
                          <DownloadIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </MuiTooltip>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                    Bar Yönü
                  </Typography>
                  <ToggleButtonGroup
                    orientation="horizontal"
                    exclusive
                    size="small"
                    value={chartOrientation}
                    onChange={(event, next) => {
                      if (!next) return;
                      setChartOrientation(next);
                    }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1,
                        py: 0.25,
                        fontSize: 12,
                      },
                    }}
                  >
                    <ToggleButton value="vertical" title="Dikey">
                      <SwapVertIcon fontSize="inherit" />
                    </ToggleButton>
                    <ToggleButton value="horizontal" title="Yatay">
                      <SwapHorizIcon fontSize="inherit" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ ...inlineControlSx, minHeight: 40 }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                    Seri Düzeni
                  </Typography>
                  <ToggleButtonGroup
                    orientation="horizontal"
                    exclusive
                    size="small"
                    value={chartStackMode}
                    onChange={(event, next) => {
                      if (!next) return;
                      setChartStackMode(next);
                    }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1,
                        py: 0.25,
                        fontSize: 12,
                      },
                    }}
                  >
                    <ToggleButton value="stacked" title="Stacked">
                      Stack
                    </ToggleButton>
                    <ToggleButton value="grouped" title="Gruplanmış">
                      Gruplu
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Box
                  sx={{
                    ...inlineControlSx,
                    minHeight: 40,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FormControl size="small" variant="outlined" sx={{ minWidth: 160 }}>
                    <InputLabel>Renk Teması</InputLabel>
                    <Select
                      label="Renk Teması"
                      value={colorThemeId}
                      onChange={(event) => {
                        const next = event.target.value;
                        const fallback = defaultColorThemeId || fallbackThemeId;
                        colorThemeOverrideRef.current = next !== fallback;
                        setColorThemeId(next);
                      }}
                      renderValue={(selected) => {
                        const theme = getThemeById(selected, themeList);
                        return (
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Typography variant="body2">{theme.label}</Typography>
                            <Stack direction="row" spacing={0.25}>
                              {(theme.palette || []).slice(0, 4).map((color, idx) => (
                                <Box
                                  key={`${theme.id}-${idx}`}
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 0.5,
                                    bgcolor: color,
                                    border: '1px solid rgba(15, 23, 42, 0.12)',
                                  }}
                                />
                              ))}
                            </Stack>
                          </Stack>
                        );
                      }}
                      sx={{ fontSize: '0.85rem' }}
                    >
                      {themeList.map((theme) => (
                        <MenuItem key={theme.id} value={theme.id}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2">{theme.label}</Typography>
                            <Stack direction="row" spacing={0.4}>
                              {theme.palette.slice(0, 5).map((color, idx) => (
                                <Box
                                  key={`${theme.id}-${idx}`}
                                  sx={{
                                    width: 14,
                                    height: 10,
                                    borderRadius: 0.5,
                                    bgcolor: color,
                                    border: '1px solid rgba(15, 23, 42, 0.12)',
                                  }}
                                />
                              ))}
                            </Stack>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Stack>
            </Box>
          )}

          {viewMode === 'table' ? (
            <Box sx={{ minWidth: minInnerWidth }}>
              <DataGrid
                rows={rows}
                columns={augColumns}
                autoHeight
                pageSizeOptions={[5,10,25,100]}
                disableRowSelectionOnClick
                slots={{ toolbar: TableCardToolbar }}
                slotProps={{
                  toolbar: {
                    csvOptions: { fileName: exportFileName, utf8WithBom: true },
                    onExportExcel: handleExportExcel,
                    excelLabel,
                    excelDisabled: exportableColumns.length === 0,
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 300 },
                  },
                }}
              />
            </Box>
          ) : (
            <Box ref={chartContentRef} sx={{ width: '100%', minHeight: 320 }}>
              {chartData.length > 0 && chartSeriesKeys.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart
                    data={chartData}
                    layout={chartLayout}
                    margin={chartMargins}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    {barsHorizontal ? (
                      <>
                        <XAxis
                          type="number"
                          tickFormatter={formatChartValue}
                          domain={chartValueDomain}
                          allowDataOverflow
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={200}
                          tick={{ fontSize: 12 }}
                          interval={0}
                        />
                      </>
                    ) : (
                      <>
                        <XAxis
                          type="category"
                          dataKey="name"
                          tick={{ fontSize: 12 }}
                          interval={0}
                        />
                        <YAxis
                          type="number"
                          tickFormatter={formatChartValue}
                          domain={chartValueDomain}
                          allowDataOverflow
                        />
                      </>
                    )}
                    <Tooltip formatter={tooltipFormatter} />
                    <Legend />
                    {chartSeriesKeys.map((seriesKey) => (
                      <Bar
                        key={seriesKey}
                        dataKey={seriesKey}
                        stackId={chartStacked ? 'stack' : undefined}
                        fill={chartSeriesColorMap[seriesKey] || chartPalette[0]}
                        name={chartSeriesNameMap[seriesKey] || seriesKey}
                        isAnimationActive={false}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    minHeight: 320,
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    {activeChartColumns.length === 0
                      ? 'Grafiğe dahil edilecek kolonları seçin.'
                      : activeChartRowKeys.length === 0
                        ? 'Grafiğe dahil edilecek satırları seçin.'
                        : 'Grafik için uygun veri bulunamadı.'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {(comparisonGroups || (significance && significance.comparisonGroups)) && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Not — Karşılaştırma Grupları: {comparisonGroups || (significance && significance.comparisonGroups)}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Menu
        anchorEl={downloadAnchorEl}
        open={Boolean(downloadAnchorEl)}
        onClose={handleCloseDownloadMenu}
        keepMounted
      >
        <MenuItem disabled={isExporting} onClick={() => handleDownloadRequest('jpeg')}>
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="JPEG (beyaz zemin)" />
        </MenuItem>
        <MenuItem disabled={isExporting} onClick={() => handleDownloadRequest('png')}>
          <ListItemIcon>
            <CropOriginalIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="PNG (şeffaf)" />
        </MenuItem>
        <MenuItem disabled={isExporting} onClick={() => handleDownloadRequest('pdf')}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="PDF" />
        </MenuItem>
        <MenuItem disabled={isExporting} onClick={() => handleDownloadRequest('pptx')}>
          <ListItemIcon>
            <SlideshowIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="PowerPoint (düzenlenebilir)" />
        </MenuItem>
      </Menu>
    </Paper>
  );
}

/* ------------ Yardımcılar (tabloya özel) ------------- */
export async function parseTableFile(file) {
  const name = (file?.name || '').toLowerCase();
  const stripBOM = (s) => (s && s.charCodeAt(0) === 0xFEFF ? s.slice(1) : s);

  if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.xlsb') || name.endsWith('.xlsm')) {
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, { type: 'array', cellDates: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
    return json;
  }

  if (name.endsWith('.csv') || name.endsWith('.txt') || name.endsWith('.tsv')) {
    const raw = stripBOM(await file.text());
    return parseCSVText(raw);
  }

  const raw = stripBOM(await file.text());
  return parseCSVText(raw);
}

export function parseCSVText(text) {
  const firstLine = (text.split(/\r?\n/)[0] || '');
  const delim = firstLine.includes(';') ? ';' : (firstLine.includes('\t') ? '\t' : ',');
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length === 0) return [];
  const headers = lines[0].split(delim).map(h => h.trim());
  const rows = lines.slice(1).map((ln, i) => {
    const parts = ln.split(delim);
    const obj = { id: i + 1 };
    headers.forEach((h, idx) => { obj[h] = (parts[idx] ?? '').trim(); });
    return obj;
  });
  return rows;
}

export function buildColumnsFromRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const sample = rows[0];
  return Object.keys(sample)
    .filter(k => k !== 'id')
    .map(k => ({ field: k, headerName: k, width: Math.max(120, String(k).length * 12) }));
}

/**
 * Tablolar sekmesinin tamamını yöneten panel
 *
 * Props:
 *  - tableCards: Array<{id,title,columns,rows,sheet?}>
 *  - onChange: (next:Array) => void
 *  - onPersist?: (chunk:Array, ctx:{index:number,total:number}) => Promise<void>
 *  - maxTablesPerSheet?: number
 *  - db?: any                 // Firestore instance
 *  - persistCtx?: {           // Kayıt yolu bağlamı
 *      organisation: string,
 *      projectID?: string,
 *      analysisID?: string    // varsa analiz altında kaydeder
 *    }
 *  - autoLoad?: boolean       // açılışta Firebase'den yükle
 *  - autoPersistOnImport?: boolean // import biter bitmez otomatik kaydet
 */
export function TableCardPanel({
  tableCards = [],
  onChange,
  onPersist = null,
  maxTablesPerSheet = 50,
  db,
  persistCtx,
  autoLoad = false,
  autoPersistOnImport = false,
  // Üst sayfadan gelen yeni props
  projectID = '',
  currentUserEmail = '',
  colTypes = [],
  filters = [],
  filterGroupPayload = null,
  showVarLabels: showVarLabelsProp,
  onToggleVarLabels,
  toId: toIdProp,
  toIds: toIdsProp,
  colorThemes,
  defaultColorThemeId,
  onNormalizedQueryChange = null,
}) {
  const fileInputRef = useRef(null);
  const toId = toIdProp || ((x) => (typeof x === 'string' ? x : (x?.id || x?.header || String(x))));
  const toIds = toIdsProp || ((arr=[]) => arr.map(toId));
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [openGroupId, setOpenGroupId] = useState(''); // sol listedeki accordion için
  const [importing, setImporting] = useState(false);
  const [importText, setImportText] = useState('');
  const [persisting, setPersisting] = useState(false);
  const [persistText, setPersistText] = useState('');
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiPromptError, setAiPromptError] = useState('');
  const [filterPanels, setFilterPanels] = useState(() => ({
    pivot: true,
    multi: false,
    undupe: false,
  }));
  const normalizedInputsRef = useRef('');
  const latestOnChangeRef = useRef(onChange);
  const mainQuestionsPayloadJsonRef = useRef('');

  const handleFilterPanelToggle = (panel) => (_, expanded) => {
    setFilterPanels((prev) => ({ ...prev, [panel]: expanded }));
  };

  const filterSurfaceSx = {
    borderRadius: 4,
    border: '1px solid rgba(148, 163, 184, 0.16)',
    boxShadow: '0 12px 24px rgba(148, 163, 184, 0.12)',
    backgroundColor: '#ffffff',
    px: { xs: 1.5, md: 2 },
    py: { xs: 1.25, md: 2 },
  };

  const filterAccordionSx = {
    borderRadius: 3,
    border: '1px solid rgba(148, 163, 184, 0.18)',
    '&:before': { display: 'none' },
    backgroundColor: 'rgba(248, 250, 252, 0.75)',
  };

  const filterRowCardSx = {
    borderRadius: 2,
    border: '1px solid rgba(148, 163, 184, 0.16)',
    backgroundColor: '#ffffff',
    px: 1.25,
    py: 1,
  };

  const controlFieldSx = {
    minWidth: 150,
    flex: '1 1 180px',
  };

  const filterChipSx = {
    fontWeight: 600,
    backgroundColor: 'rgba(191, 219, 254, 0.6)',
    color: '#1f2937',
  };

  const renderSummaryChips = (items = []) => (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{ ml: { xs: 0, md: 1.5 }, flexWrap: 'wrap', rowGap: 0.5 }}
      useFlexGap
    >
      {items
        .filter(Boolean)
        .map(({ key, label }) => (
          <Chip key={key} label={label} size="small" sx={filterChipSx} />
        ))}
    </Stack>
  );

  // --- Firestore-backed pivot options state ---
  // Auto-load latest server-generated tables from Firestore when requested
  useEffect(() => {
    latestOnChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!autoLoad || !db || !projectID) {
      return undefined;
    }
    let mounted = true;
    async function loadLatest() {
      try {
        setPersisting(true);
        setPersistText('Sunucudan tablolar yükleniyor…');
        const { tables } = await readLatestReportingRun(db, projectID);
        const cards = tables
          .map((t) => tableDocToCard(t))
          .filter(Boolean);
        if (mounted && Array.isArray(cards)) {
          const handler = latestOnChangeRef.current;
          if (typeof handler === 'function') {
            handler(cards);
          }
        }
      } catch (err) {
        console.warn('[TableCardPanel] autoLoad latest run failed:', err);
      } finally {
        if (mounted) {
          setPersisting(false);
          setPersistText('');
        }
      }
    }
    loadLatest();
    return () => {
      mounted = false;
    };
  }, [autoLoad, db, projectID]);
  const [dbColTypes, setDbColTypes] = useState([]);
  const [dbLeafOptions, setDbLeafOptions] = useState([]); // [{id, header}]
  const [dbRowGroupOptions, setDbRowGroupOptions] = useState([]); // [string]
  const [mainQuestionsPayload, setMainQuestionsPayload] = useState(null);
  const [showVarLabelsState, setShowVarLabelsState] = useState(true);

  const isShowVarLabelsControlled = typeof showVarLabelsProp === 'boolean';
  const showVarLabels = isShowVarLabelsControlled ? showVarLabelsProp : showVarLabelsState;
  const handleShowVarLabelsChange = (nextValue) => {
    if (!isShowVarLabelsControlled) {
      setShowVarLabelsState(nextValue);
    }
    if (typeof onToggleVarLabels === 'function') {
      onToggleVarLabels(nextValue);
    }
  };


  const metaForLabels = useMemo(
      () =>
        (Array.isArray(colTypes) && colTypes.length
          ? colTypes
          : (window.__columnTypes || window.__projectRawData || [])),
      [colTypes]
  );

  useEffect(() => {
    const payloadFromDb = extractMainQuestionsPayload(dbColTypes);
    const payloadFromProps = extractMainQuestionsPayload(colTypes);
    const nextPayload = payloadFromDb || payloadFromProps || null;
    const nextJson = nextPayload ? JSON.stringify(nextPayload) : '';
    if (mainQuestionsPayloadJsonRef.current === nextJson) return;
    mainQuestionsPayloadJsonRef.current = nextJson;
    setMainQuestionsPayload(nextPayload);
  }, [dbColTypes, colTypes]);

  const slimMainQuestionsPayload = React.useMemo(() => {
    if (!mainQuestionsPayload || typeof mainQuestionsPayload !== 'object') return null;
    const questions = Array.isArray(mainQuestionsPayload.questions)
      ? mainQuestionsPayload.questions
      : [];
    const sanitizedQuestions = questions
      .map((q) => {
        if (!q || typeof q !== 'object') return null;
        const id = q.id != null ? String(q.id).trim() : '';
        const label = q.label != null ? String(q.label).trim() : '';
        if (!id) return null;
        return { id, label };
      })
      .filter(Boolean);
    if (!sanitizedQuestions.length) return null;
    return { questions: sanitizedQuestions };
  }, [mainQuestionsPayload]);

  const fmt = useVarLabelFormatter(metaForLabels, showVarLabels);

  const [undupeQuestions, setUndupeQuestions] = useState(() => []);

  const baseColumnMetaIndex = useMemo(() => {
    const exact = new Map();
    const lower = new Map();

    const shouldReplace = (current, candidate) => {
      if (!current) return true;
      if (!candidate) return false;
      const currentHasType = current.type != null && current.type !== '';
      const candidateHasType = candidate.type != null && candidate.type !== '';
      if (!currentHasType && candidateHasType) return true;
      if (currentHasType && !candidateHasType) return false;

      const currentLikertFlag = Boolean(
        current.isLikert ||
        current.groupLikert ||
        (Array.isArray(current.likertScale) && current.likertScale.length) ||
        (Array.isArray(current.groupLikertScale) && current.groupLikertScale.length) ||
        current.questionType === 'likert_scale'
      );
      const candidateLikertFlag = Boolean(
        candidate.isLikert ||
        candidate.groupLikert ||
        (Array.isArray(candidate.likertScale) && candidate.likertScale.length) ||
        (Array.isArray(candidate.groupLikertScale) && candidate.groupLikertScale.length) ||
        candidate.questionType === 'likert_scale'
      );
      if (!currentLikertFlag && candidateLikertFlag) return true;
      if (currentLikertFlag && !candidateLikertFlag) return false;
      return false;
    };

    const registerKey = (key, col) => {
      if (key == null) return;
      const str = String(key).trim();
      if (!str) return;
      const existing = exact.get(str);
      if (!existing || shouldReplace(existing, col)) {
        exact.set(str, col);
      }
      const lowerKey = str.toLowerCase();
      const existingLower = lower.get(lowerKey);
      if (!existingLower || shouldReplace(existingLower, col)) {
        lower.set(lowerKey, col);
      }
    };

    const registerColumn = (col) => {
      if (!col) return;
      const candidateKeys = [
        col.id,
        col.header,
        col.headerName,
        col.name,
        col.field,
        col.text,
      ];
      candidateKeys.forEach((key) => registerKey(key, col));
      if (Array.isArray(col.aliases)) {
        col.aliases.forEach((alias) => registerKey(alias, col));
      }
      if (Array.isArray(col.columns)) {
        col.columns.forEach((alias) => registerKey(alias, col));
      }
    };

    const sources = [];
    if (Array.isArray(colTypes) && colTypes.length) sources.push(colTypes);
    if (Array.isArray(dbColTypes) && dbColTypes.length) sources.push(dbColTypes);
    if (Array.isArray(metaForLabels) && metaForLabels.length) sources.push(metaForLabels);

    sources.forEach((list) => {
      list.forEach((col) => registerColumn(col));
    });

    return { exact, lower };
  }, [colTypes, dbColTypes, metaForLabels]);

  const baseColumnMetaValues = useMemo(() => {
    const seen = new Map();
    baseColumnMetaIndex.exact.forEach((col, key) => {
      if (!col) return;
      const primary =
        col?.id != null && String(col.id).trim() !== ''
          ? String(col.id)
          : col?.header != null && String(col.header).trim() !== ''
            ? String(col.header)
            : String(key);
      if (!primary) return;
      if (!seen.has(primary)) {
        seen.set(primary, col);
      }
    });
    return Array.from(seen.values());
  }, [baseColumnMetaIndex]);

  const idToHeaderMap = useMemo(() => {
    const map = {};
    baseColumnMetaValues.forEach((col) => {
      if (!col) return;
      const idRaw =
        col?.id != null && String(col.id).trim() !== ''
          ? String(col.id).trim()
          : col?.header != null && String(col.header).trim() !== ''
            ? String(col.header).trim()
            : '';
      if (!idRaw) return;
      const headerRaw =
        col?.header != null && String(col.header).trim() !== ''
          ? String(col.header).trim()
          : idRaw;
      map[idRaw] = headerRaw;
    });
    return map;
  }, [baseColumnMetaValues]);

  const questionOptionsById = useMemo(() => {
    const exact = new Map();
    const lower = new Map();

    const register = (key, opts) => {
      if (key == null) return;
      const str = String(key).trim();
      if (!str) return;
      if (!exact.has(str)) exact.set(str, opts);
      const low = str.toLowerCase();
      if (!lower.has(low)) lower.set(low, opts);
    };

    baseColumnMetaValues.forEach((col) => {
      const opts = extractOptionsFromMeta(col);
      if (!Array.isArray(opts) || !opts.length) return;
      const cloned = opts.map((opt) => ({ ...opt }));
      register(col?.id, cloned);
      register(col?.header, cloned);
      if (Array.isArray(col?.aliases)) {
        col.aliases.forEach((alias) => register(alias, cloned));
      }
      if (Array.isArray(col?.columns)) {
        col.columns.forEach((alias) => register(alias, cloned));
      }
    });

    return { exact, lower };
  }, [baseColumnMetaValues]);

  const getQuestionOptions = React.useCallback(
    (questionId) => {
      if (questionId == null) return [];
      const str = String(questionId).trim();
      if (!str) return [];
      return (
        questionOptionsById.exact.get(str) ||
        questionOptionsById.lower.get(str.toLowerCase()) ||
        []
      );
    },
    [questionOptionsById]
  );

  const normalizedUndupeState = useMemo(() => {
    const list = Array.isArray(undupeQuestions) ? undupeQuestions : [];
    return list
      .map((item, idx) => {
        const baseQuestionId =
          item?.baseQuestionId != null ? String(item.baseQuestionId).trim() : '';
        const newQuestionLabel =
          item?.newQuestionLabel != null ? String(item.newQuestionLabel).trim() : '';
        const hiddenOptionValues = Array.isArray(item?.hiddenOptionValues)
          ? Array.from(
              new Set(
                item.hiddenOptionValues
                  .map((val) => String(val).trim())
                  .filter((val) => val !== '')
              )
            )
          : [];

        const mergesRaw = Array.isArray(item?.merges) ? item.merges : [];
        const normalizedMerges = mergesRaw
          .map((merge, mergeIdx) => {
            const mergeId =
              merge?.id != null && String(merge.id).trim() !== ''
                ? String(merge.id).trim()
                : `${item?.id || 'undupe'}_merge_${mergeIdx}`;
            const mergeOptionValues = Array.isArray(merge?.mergeOptionValues)
              ? Array.from(
                  new Set(
                    merge.mergeOptionValues
                      .map((val) => String(val).trim())
                      .filter((val) => val !== '')
                  )
                )
              : [];
            if (mergeOptionValues.length < MIN_UNDUPE_MERGE_COUNT) return null;
            const newOptionLabel =
              merge?.newOptionLabel != null ? String(merge.newOptionLabel).trim() : '';
            return {
              id: mergeId,
              mergeOptionValues,
              newOptionLabel,
            };
          })
          .filter(Boolean);

        const fallbackId =
          item?.id != null && String(item.id).trim() !== ''
            ? String(item.id).trim()
            : `${generateUndupeLocalId()}_${idx}`;

        return {
          id: fallbackId,
          baseQuestionId,
          newQuestionLabel,
          hiddenOptionValues,
          merges: normalizedMerges,
        };
      })
      .filter(
        (cfg) =>
          cfg.baseQuestionId &&
          Array.isArray(cfg.merges) &&
          cfg.merges.length > 0
      );
  }, [undupeQuestions]);

  const undupeRuntime = useMemo(() => {
    const entries = [];
    normalizedUndupeState.forEach((cfg) => {
      const baseId = cfg.baseQuestionId;
      if (!baseId) return;

      const baseMeta =
        baseColumnMetaIndex.exact.get(baseId) ||
        baseColumnMetaIndex.lower.get(baseId.toLowerCase()) ||
        null;
      if (!baseMeta) return;

      const baseOptions = getQuestionOptions(baseId);
      if (!Array.isArray(baseOptions) || baseOptions.length === 0) return;

      const baseOptionLabelByValue = new Map();
      baseOptions.forEach((opt) => {
        const val = String(opt.value);
        if (!val) return;
        const label = opt.label != null ? String(opt.label) : val;
        baseOptionLabelByValue.set(val, label);
      });

      const hiddenValues = Array.isArray(cfg.hiddenOptionValues)
        ? cfg.hiddenOptionValues.map((val) => String(val))
        : [];
      const hiddenSet = new Set(hiddenValues);
      const hiddenLabels = hiddenValues.map(
        (val) => baseOptionLabelByValue.get(val) ?? val
      );

      const baseLabel =
        fmt(baseMeta?.header || baseMeta?.id || baseId) || (baseMeta?.header || baseId);

      const questionId = slugifyUndupeId(`${baseId}_${cfg.id}`);
      const questionLabel =
        cfg.newQuestionLabel && cfg.newQuestionLabel.trim() !== ''
          ? cfg.newQuestionLabel
          : `${baseLabel} (Undupe)`;

      const baseOptionDefs = baseOptions
        .map((opt) => {
          const val = String(opt.value);
          if (!val || hiddenSet.has(val)) return null;
          const label = opt.label != null ? String(opt.label) : val;
          return {
            id: val,
            value: val,
            label,
            sourceValues: [val],
            sourceLabels: [label],
            isMerged: false,
          };
        })
        .filter(Boolean);

      const mergedOptionDefs = [];
      (cfg.merges || []).forEach((merge, mergeIdx) => {
        const mergeSet = new Set((merge.mergeOptionValues || []).map((val) => String(val)));
        if (mergeSet.size < MIN_UNDUPE_MERGE_COUNT) return;

        const mergeLabels = Array.from(mergeSet).map(
          (code) => baseOptionLabelByValue.get(code) ?? code
        );
        const mergedOptionLabel =
          merge.newOptionLabel && merge.newOptionLabel.trim() !== ''
            ? merge.newOptionLabel
            : mergeLabels.filter(Boolean).join(' + ') || `${questionLabel} ${mergeIdx + 1}`;

        const rawMergeId = merge.id || `${questionId}_merge_${mergeIdx + 1}`;
        const mergedValueSlug = slugifyUndupeId(rawMergeId, '');
        const mergedOptionValue = `${questionId}_${mergedValueSlug || `m${mergeIdx + 1}`}`;

        mergedOptionDefs.push({
          id: rawMergeId,
          value: mergedOptionValue,
          label: mergedOptionLabel,
          sourceValues: Array.from(mergeSet),
          sourceLabels: mergeLabels,
          isMerged: true,
          mergeId: rawMergeId,
        });
      });

      if (!mergedOptionDefs.length) return;

      const optionDefs = [
        ...baseOptionDefs,
        ...mergedOptionDefs,
      ];

      const primaryMergedOption = mergedOptionDefs[0] || null;

      const columnType = {
        id: questionId,
        header: questionLabel,
        type: baseMeta?.type || baseMeta?.questionType || 'multi-select data',
        questionType: baseMeta?.questionType || baseMeta?.type || 'multi-select data',
        grouptype: baseMeta?.grouptype || 'multiplechoice',
        sourceQuestionId: baseMeta?.id || baseId,
        sourceQuestionHeader: baseMeta?.header || baseId,
        isUndupe: true,
        undupe: {
          baseQuestionId: baseMeta?.id || baseId,
          baseHeader: baseMeta?.header || baseId,
          merges: mergedOptionDefs.map((opt) => ({
            id: opt.mergeId,
            value: opt.value,
            label: opt.label,
            sourceValues: opt.sourceValues,
            sourceLabels: opt.sourceLabels,
          })),
          options: optionDefs.map((opt) => ({
            id: opt.id ?? opt.value,
            value: opt.value,
            label: opt.label,
            sourceValues: opt.sourceValues,
            sourceLabels: opt.sourceLabels,
          })),
          mergedOption: primaryMergedOption
            ? {
                id: primaryMergedOption.mergeId,
                value: primaryMergedOption.value,
                label: primaryMergedOption.label,
                sourceValues: primaryMergedOption.sourceValues,
                sourceLabels: primaryMergedOption.sourceLabels,
              }
            : null,
          hiddenValues: Array.from(hiddenSet),
          hiddenLabels,
          inheritRemaining: true,
        },
        options: optionDefs.map((opt) => opt.label),
        optionsDetailed: optionDefs.map((opt) => ({
          id: opt.id ?? opt.value,
          value: opt.value,
          label: opt.label,
          sourceValues: opt.sourceValues,
          sourceLabels: opt.sourceLabels,
          isMerged: opt.isMerged,
        })),
      };

      const apiPayload = {
        id: questionId,
        header: questionLabel,
        baseQuestionId: baseMeta?.id || baseId,
      baseHeader: baseMeta?.header || baseId,
      merges: mergedOptionDefs.map((opt) => ({
        id: opt.mergeId,
        value: opt.value,
        label: opt.label,
        sourceValues: opt.sourceValues,
        sourceLabels: opt.sourceLabels,
      })),
      mergedOption: primaryMergedOption
        ? {
            id: primaryMergedOption.mergeId,
            value: primaryMergedOption.value,
            label: primaryMergedOption.label,
            sourceValues: primaryMergedOption.sourceValues,
            sourceLabels: primaryMergedOption.sourceLabels,
          }
        : null,
      options: optionDefs.map((opt) => ({
        id: opt.id ?? opt.value,
        value: opt.value,
        label: opt.label,
        sourceValues: opt.sourceValues,
        sourceLabels: opt.sourceLabels,
        isMerged: opt.isMerged,
      })),
      hiddenValues: Array.from(hiddenSet),
      hiddenLabels,
    };

      entries.push({
        config: cfg,
        columnType,
        apiPayload,
        selectOption: { id: questionId, header: questionLabel },
      });
    });
    return entries;
  }, [normalizedUndupeState, baseColumnMetaIndex, getQuestionOptions, fmt]);

  const undupeColumnMetaIndex = useMemo(() => {
    const exact = new Map();
    const lower = new Map();
    undupeRuntime.forEach(({ columnType }) => {
      if (!columnType) return;
      const register = (key) => {
        if (key == null) return;
        const str = String(key).trim();
        if (!str) return;
        if (!exact.has(str)) exact.set(str, columnType);
        const low = str.toLowerCase();
        if (!lower.has(low)) lower.set(low, columnType);
      };
      register(columnType.id);
      register(columnType.header);
      if (Array.isArray(columnType.aliases)) {
        columnType.aliases.forEach(register);
      }
    });
    return { exact, lower };
  }, [undupeRuntime]);

  const resolveColumnMeta = React.useCallback(
    (rawId) => {
      if (rawId == null) return null;
      const str = String(rawId).trim();
      if (!str) return null;
      const baseMeta =
        baseColumnMetaIndex.exact.get(str) ||
        baseColumnMetaIndex.lower.get(str.toLowerCase());
      if (baseMeta) return baseMeta;
      return (
        undupeColumnMetaIndex.exact.get(str) ||
        undupeColumnMetaIndex.lower.get(str.toLowerCase()) ||
        null
      );
    },
    [baseColumnMetaIndex, undupeColumnMetaIndex]
  );

  const undupeSelectOptions = useMemo(
    () => undupeRuntime.map((entry) => entry.selectOption),
    [undupeRuntime]
  );

  const undupeApiPayload = useMemo(
    () => undupeRuntime.map((entry) => entry.apiPayload),
    [undupeRuntime]
  );

  const undupeColumnTypes = useMemo(
    () => undupeRuntime.map((entry) => entry.columnType),
    [undupeRuntime]
  );

  const columnMetadataForApi = useMemo(() => {
    const activeCols = Array.isArray(colTypes)
      ? colTypes.filter(
          (c) =>
            c && (c.active !== false || ['griddata', 'multigriddata'].includes(c.grouptype))
        )
      : [];

    const dbCols = Array.isArray(dbColTypes)
      ? dbColTypes.filter(
          (c) =>
            c && (c.active !== false || ['griddata', 'multigriddata'].includes(c.grouptype))
        )
      : [];

    const registerColumns = (list, map) => {
      (Array.isArray(list) ? list : []).forEach((col) => {
        if (!col) return;
        const key = col.id || col.header;
        if (!key) return;
        if (!map.has(key)) {
          map.set(key, col);
        }
      });
    };

    const combinedMap = new Map();
    registerColumns(activeCols, combinedMap);
    registerColumns(dbCols, combinedMap);
    registerColumns(undupeColumnTypes, combinedMap);

    const combinedCols = Array.from(combinedMap.values());

    const metaForLabelsLocal = combinedCols.map((col) => ({
      id: col?.id,
      header: col?.header,
      aliases: col?.aliases,
      options: col?.options,
      groupname: col?.groupname,
      groupLabel: col?.groupLabel,
      question: col?.question,
      questionText: col?.questionText,
      text: col?.text,
      shortText: col?.shortText,
      label: col?.label,
      description: col?.description,
      likertScale: col?.likertScale,
      groupLikertScale: col?.groupLikertScale,
      valueLabels: col?.valueLabels,
      type: col?.type,
      grouptype: col?.grouptype,
      manualUngroup: col?.manualUngroup,
    }));

    const slimCols = combinedCols.map((col) => {
      const base = {
        id: col?.id,
        header: col?.header,
        grouptype: col?.grouptype,
        type: col?.type,
        questionType: col?.questionType,
        manualUngroup: col?.manualUngroup,
        active: col?.active,
        options: col?.options,
        alias: col?.alias,
        aliases: col?.aliases,
        groupname: col?.groupname,
        groupLabel: col?.groupLabel,
        likertScale: col?.likertScale,
        groupLikertScale: col?.groupLikertScale,
        valueLabels: col?.valueLabels,
      };
      if (Array.isArray(col?.columns)) {
        base.columns = col.columns
          .map((c) => (typeof c === 'string' ? c : c?.header || c?.id))
          .filter(Boolean);
      }
      return base;
    });

    const addLikertMetadata = (list) => {
      if (!Array.isArray(list)) return list;
      return list.map((col) => {
        if (!col) return col;
        const match = metaForLabelsLocal.find(
          (m) => m.id === col.id || m.header === col.header
        );
        if (!match) return col;
        return {
          ...col,
          likertScale: col.likertScale || match.likertScale,
          groupLikertScale: col.groupLikertScale || match.groupLikertScale,
          valueLabels: col.valueLabels || match.valueLabels,
          options: col.options || match.options,
          groupname: col.groupname || match.groupname,
          groupLabel: col.groupLabel || match.groupLabel,
          question: col.question || match.question,
          questionText: col.questionText || match.questionText,
          text: col.text || match.text,
          shortText: col.shortText || match.shortText,
        };
      });
    };

    const normalizedSlimCols = addLikertMetadata(slimCols);

    const normalizeLikertScale = (rows) => {
      if (!Array.isArray(rows)) return [];
      return rows
        .map((row, idx) => {
          if (row == null) return null;

          if (typeof row === 'object' && !Array.isArray(row)) {
            const rawValue = row.value ?? row.score ?? row.weight;
            const numericValue = Number(rawValue);
            const fallbackValue = Number.isFinite(numericValue) ? Number(numericValue) : idx + 1;
            const code = row.code != null
              ? String(row.code)
              : row.value != null
                ? String(row.value)
                : '';
            const label = row.label != null
              ? String(row.label)
              : row.text != null
                ? String(row.text)
                : '';

            if (!code && !label) return null;
            return { code, label, value: fallbackValue };
          }

          const str = String(row);
          if (!str.trim()) return null;
          return { code: str, label: str, value: idx + 1 };
        })
        .filter(Boolean);
    };

    const deriveScaleFromValueLabels = (valueLabels) => {
      if (!valueLabels || typeof valueLabels !== 'object') return [];
      const entries = Object.entries(valueLabels).map(([code, label]) => ({
        code: String(code),
        label: label != null ? String(label) : String(code),
        value: Number(code),
      }));
      return normalizeLikertScale(entries);
    };

    const deriveScaleFromOptions = (options) => {
      if (!Array.isArray(options)) return [];
      const parsed = options
        .map((opt, idx) => {
          const str = String(opt ?? '').trim();
          if (!str) return null;
          const match = str.match(/^\s*(\d+(?:[.,]\d+)?)\s*[-–:]?\s*(.*)$/);
          const code = match ? match[1] : str;
          const label = match ? match[2] : str;
          return {
            code: String(code),
            label: label ? String(label).trim() : String(code),
            value: Number(code.replace(',', '.')),
            _fallbackIndex: idx,
          };
        })
        .filter(Boolean);

      return normalizeLikertScale(
        parsed.map((item) => ({
          code: item.code,
          label: item.label,
          value: Number.isFinite(item.value) ? Number(item.value) : item._fallbackIndex + 1,
        }))
      );
    };

    const buildLikertScale = (column, { preferGroup = false } = {}) => {
      if (!column) return [];

      const tryOrder = [];

      if (preferGroup) {
        tryOrder.push(column.groupLikertScale);
        tryOrder.push(column.likertScale);
      } else {
        tryOrder.push(column.likertScale);
        tryOrder.push(column.groupLikertScale);
      }

      for (const candidate of tryOrder) {
        const normalized = normalizeLikertScale(candidate);
        if (normalized.length) return normalized;
      }

      const fromValueLabels = deriveScaleFromValueLabels(column.valueLabels);
      if (fromValueLabels.length) return fromValueLabels;

      const fromOptions = deriveScaleFromOptions(column.options);
      if (fromOptions.length) return fromOptions;

      return [];
    };

    const likertGroupsMap = new Map();

    normalizedSlimCols.forEach((c) => {
      if (!c) return;

      const base = {
        id: c.id,
        header: c.header,
        options: c.options,
        question: c.question,
        questionText: c.questionText,
        text: c.text,
        shortText: c.shortText,
        likertScale: c.likertScale,
        groupLikertScale: c.groupLikertScale,
        valueLabels: c.valueLabels,
        groupname: c.groupname,
        groupLabel: c.groupLabel,
      };

      const preferGroup = c.groupLikertScale && !c.likertScale;
      const normalizedLikert = buildLikertScale(c, { preferGroup: false });
      const normalizedGroupLikert = buildLikertScale(c, { preferGroup: true });

      if (normalizedLikert.length) {
        base.likertScale = normalizedLikert;
      }
      if (normalizedGroupLikert.length) {
        base.groupLikertScale = normalizedGroupLikert;
      }

      if (c?.grouptype === 'multiplechoice' || c?.type === 'multi-select data') {
        const opts = Array.isArray(c.options) ? c.options.slice(0, 100) : undefined;
        if (opts) base.options = opts;
      }

      if (c.groupname) {
        const groupKey = c.groupname;
        const existing = likertGroupsMap.get(groupKey) || {
          id: groupKey,
          name: c.groupname,
          columns: [],
          scale: normalizedGroupLikert.length ? normalizedGroupLikert : normalizedLikert,
        };
        existing.columns = Array.from(new Set([...(existing.columns || []), base.header]));
        if (!existing.scale?.length) {
          existing.scale = normalizedLikert;
        }
        if (!existing.groupLabel && c.groupLabel) {
          existing.groupLabel = c.groupLabel;
        }
        likertGroupsMap.set(groupKey, existing);
      }
    });

    const likertQuestions = activeCols
      .map((c) => {
        if (!c) return null;
        const header = c.header || c.id;
        if (!header) return null;

        const scale = buildLikertScale(c);
        if (!scale.length) return null;

        if (!(c.isLikert || c.groupLikert)) {
          return null;
        }

        return {
          header,
          id: c.id || c.header || header,
          groupname: c.groupname || '',
          groupLabel: typeof c.groupLabel === 'string' ? c.groupLabel : '',
          scale,
        };
      })
      .filter(Boolean);

    const likertGroups = Array.from(likertGroupsMap.values()).filter(
      (grp) =>
        Array.isArray(grp.columns) &&
        grp.columns.length &&
        Array.isArray(grp.scale) &&
        grp.scale.length
    );

    return {
      slimCols: normalizedSlimCols,
      columnTypesCompact: compactColumnTypesForApi(normalizedSlimCols),
      likertGroups,
      likertQuestions,
    };
  }, [colTypes, dbColTypes, undupeColumnTypes]);

  const getOptionLabel = React.useCallback(
    (questionId, value) => {
      const opts = getQuestionOptions(questionId);
      const str = String(value ?? '').trim();
      if (!str) return value;
      const found = opts.find((opt) => String(opt.value) === str);
      return found ? found.label : value;
    },
    [getQuestionOptions]
  );

  const isLikertQuestion = React.useCallback(
    (rawId) => {
      const meta = resolveColumnMeta(rawId);
      if (!meta) return false;
      if (meta.type === 'likert_scale' || meta.questionType === 'likert_scale') return true;
      if (meta.isLikert || meta.groupLikert) return true;
      if (Array.isArray(meta.likertScale) && meta.likertScale.length) return true;
      if (Array.isArray(meta.groupLikertScale) && meta.groupLikertScale.length) return true;
      return false;
    },
    [resolveColumnMeta]
  );

  const areAllLikertQuestions = React.useCallback(
    (questionIds = []) => {
      const arr = Array.isArray(questionIds) ? questionIds : [];
      if (!arr.length) return false;
      return arr.every((qid) => isLikertQuestion(qid));
    },
    [isLikertQuestion]
  );

  // --- Helpers: normalize option objects to ensure unique, string IDs ---
  const normalizeOptions = React.useCallback((opts = []) => {
    const out = [];
    for (let i = 0; i < (opts?.length || 0); i++) {
      const o = opts[i] || {};
      const rawId = o.id ?? o.header ?? o.field ?? o.name ?? `opt_${i}`;
      if (rawId == null || rawId === '') continue;
      const id = String(rawId);
      const header = o.header ?? o.name ?? o.field ?? id;
      if (!out.some(x => x.id === id)) out.push({ id, header });
    }
    return out;
  }, []);

  const createEmptyUndupeMerge = React.useCallback(
    () => ({
      id: generateUndupeMergeLocalId(),
      mergeOptionValues: [],
      newOptionLabel: '',
    }),
    []
  );


  const undupeBaseQuestionOptions = useMemo(() => {
    const items = [];
    const seen = new Set();
    baseColumnMetaValues.forEach((col) => {
      if (!col) return;
      const options = extractOptionsFromMeta(col);
      if (!Array.isArray(options) || options.length === 0) return;
      if (col?.isUndupe) return;
      const id =
        col?.id != null && String(col.id).trim() !== ''
          ? String(col.id).trim()
          : col?.header != null && String(col.header).trim() !== ''
            ? String(col.header).trim()
            : '';
      if (!id || seen.has(id)) return;
      seen.add(id);
      items.push({
        id,
        header: col?.header || col?.name || id,
      });
    });
    return normalizeOptions(items);
  }, [baseColumnMetaValues, normalizeOptions]);

  const fallbackColumnOptions = useMemo(() => {
    const items = [];
    baseColumnMetaValues.forEach((col, idx) => {
      if (!col) return;
      const rawId =
        col?.id ?? col?.header ?? col?.name ?? col?.field ?? `col_${idx}`;
      if (rawId == null || rawId === '') return;
      const id = String(rawId).trim();
      if (!id) return;
      const headerRaw =
        col?.header ?? col?.name ?? col?.label ?? col?.field ?? '';
      const header = headerRaw ? String(headerRaw).trim() : id;
      items.push({ id, header });
    });
    return normalizeOptions(items);
  }, [baseColumnMetaValues, normalizeOptions]);

  // --- Pivot builder state (moved from upper page) ---
  const [pivotRows, setPivotRows] = useState(() => [
    { rowCols: [], colCols: [], weightCol: '' }
  ]);

  const addPivotRow = () => setPivotRows(p => [...p, { rowCols: [], colCols: [], weightCol: '' }]);
  const removePivotRow = (idx) => setPivotRows(p => p.filter((_,i)=>i!==idx));
  const handlePivotChange = (idx, key, value) => {
    setPivotRows(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const targetKey = key === 'rowCol' ? 'rowCols' : key;
      if (targetKey === 'rowCols' || targetKey === 'colCols') {
        const arr = Array.isArray(value) ? value : (value == null ? [] : [value]);
        const uniqueValues = Array.from(
          new Set(
            arr
              .filter(v => v !== undefined && v !== null && String(v).trim() !== '')
              .map(v => String(v))
          )
        );
        return { ...it, [targetKey]: uniqueValues };
      }
      // single-selects stored as strings
      return { ...it, [targetKey]: value == null ? '' : String(value) };
    }));
  };

  const [multiQuestionRows, setMultiQuestionRows] = useState(() => [
    { questions: [], weightCol: '', aggregation: [] }
  ]);

  const addMultiQuestionRow = () =>
    setMultiQuestionRows((prev) => [...prev, { questions: [], weightCol: '', aggregation: [] }]);
  const removeMultiQuestionRow = (idx) =>
    setMultiQuestionRows((prev) => prev.filter((_, i) => i !== idx));
  const handleMultiQuestionChange = (idx, key, value) => {
    setMultiQuestionRows((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        if (key === 'questions') {
          const arr = Array.isArray(value) ? value : (value == null ? [] : [value]);
          const normalized = Array.from(
            new Set(arr.map((val) => String(val)).filter(Boolean))
          );
          const likertEligible = areAllLikertQuestions(normalized);
          const existingAgg = Array.isArray(row.aggregation)
            ? row.aggregation
            : (row.aggregation ? [String(row.aggregation)] : []);
          const sanitizedAgg = existingAgg.filter((val) => LIKERT_CALC_VALUE_SET.has(val));
          return {
            ...row,
            questions: normalized,
            aggregation: likertEligible ? sanitizedAgg : [],
          };
        }
        if (key === 'aggregation') {
          const arr = Array.isArray(value)
            ? value
            : (value == null ? [] : [value]);
          const normalized = Array.from(
            new Set(arr.map((val) => String(val)).filter(Boolean))
          ).filter((val) => LIKERT_CALC_VALUE_SET.has(val));
          return { ...row, aggregation: normalized };
        }
        return { ...row, [key]: value == null ? '' : String(value) };
      })
    );
  };

  const addUndupeQuestion = React.useCallback(() => {
    setUndupeQuestions((prev) => [
      ...prev,
      {
        id: generateUndupeLocalId(),
        baseQuestionId: '',
        newQuestionLabel: '',
        hiddenOptionValues: [],
        merges: [createEmptyUndupeMerge()],
      },
    ]);
  }, [createEmptyUndupeMerge]);

  const removeUndupeQuestion = React.useCallback((id) => {
    setUndupeQuestions((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleUndupeFieldChange = React.useCallback(
    (id, key, rawValue) => {
      setUndupeQuestions((prev) =>
        prev.map((item) => {
          if (item.id !== id) return item;
          const next = { ...item };
          if (key === 'baseQuestionId') {
            const baseId = rawValue != null ? String(rawValue).trim() : '';
            next.baseQuestionId = baseId;
            next.hiddenOptionValues = [];
            next.merges = [createEmptyUndupeMerge()];
            if (!item.newQuestionLabel || !item.newQuestionLabel.trim()) {
              const baseLabel = baseId ? (fmt(baseId) || baseId) : '';
              next.newQuestionLabel = baseId ? `${baseLabel} (Undupe)` : '';
            }
            return next;
          }
          if (key === 'newQuestionLabel') {
            next.newQuestionLabel = rawValue != null ? String(rawValue).trim() : '';
            return next;
          }
          if (key === 'hiddenOptionValues') {
            const arr = Array.isArray(rawValue)
              ? rawValue
              : rawValue == null
                ? []
                : [rawValue];
            next.hiddenOptionValues = Array.from(
              new Set(arr.map((val) => String(val).trim()).filter((val) => val !== ''))
            );
            return next;
          }
          return next;
        })
      );
    },
    [createEmptyUndupeMerge, fmt]
  );

  const addUndupeMerge = React.useCallback(
    (questionId) => {
      setUndupeQuestions((prev) =>
        prev.map((item) => {
          if (item.id !== questionId) return item;
          const merges = Array.isArray(item.merges) ? item.merges : [];
          return {
            ...item,
            merges: [...merges, createEmptyUndupeMerge()],
          };
        })
      );
    },
    [createEmptyUndupeMerge]
  );

  const removeUndupeMerge = React.useCallback(
    (questionId, mergeId) => {
      setUndupeQuestions((prev) =>
        prev.map((item) => {
          if (item.id !== questionId) return item;
          const merges = (Array.isArray(item.merges) ? item.merges : []).filter(
            (merge) => merge.id !== mergeId
          );
          return {
            ...item,
            merges: merges.length ? merges : [createEmptyUndupeMerge()],
          };
        })
      );
    },
    [createEmptyUndupeMerge]
  );

  const handleUndupeMergeChange = React.useCallback(
    (questionId, mergeId, key, rawValue) => {
      setUndupeQuestions((prev) =>
        prev.map((item) => {
          if (item.id !== questionId) return item;
          const merges = (Array.isArray(item.merges) ? item.merges : []).map((merge) => {
            if (merge.id !== mergeId) return merge;
            const nextMerge = { ...merge };
            if (key === 'mergeOptionValues') {
              const arr = Array.isArray(rawValue)
                ? rawValue
                : rawValue == null
                  ? []
                  : [rawValue];
              const normalized = Array.from(
                new Set(arr.map((val) => String(val).trim()).filter((val) => val !== ''))
              );
              nextMerge.mergeOptionValues = normalized;
              if ((!nextMerge.newOptionLabel || !nextMerge.newOptionLabel.trim()) && item.baseQuestionId) {
                const labels = normalized.map((val) => getOptionLabel(item.baseQuestionId, val));
                const mergedLabel = labels.filter(Boolean).join(' + ');
                if (mergedLabel) {
                  nextMerge.newOptionLabel = mergedLabel;
                }
              }
              return nextMerge;
            }
            if (key === 'newOptionLabel') {
              nextMerge.newOptionLabel = rawValue != null ? String(rawValue).trim() : '';
              return nextMerge;
            }
            return nextMerge;
          });
          return { ...item, merges };
        })
      );
    },
    [getOptionLabel]
  );

  const buildNormalizedQueryState = React.useCallback(() => {
    const pivotState = (Array.isArray(pivotRows) ? pivotRows : [])
      .map((row) => {
        const rawRowCols = Array.isArray(row.rowCols)
          ? row.rowCols
          : (row.rowCol ? [row.rowCol] : []);
        const normalizedRowCols = Array.from(
          new Set(
            rawRowCols
              .map((val) => toId(val))
              .filter((val) => val != null && String(val).trim() !== '')
              .map((val) => String(val))
          )
        );
        if (!normalizedRowCols.length) return null;

        const rawColCols = Array.isArray(row.colCols) ? row.colCols : [];
        const normalizedColCols = Array.from(
          new Set(
            rawColCols
              .map((val) => toId(val))
              .filter((val) => val != null && String(val).trim() !== '')
              .map((val) => String(val))
          )
        );

        const weight = row.weightCol ? toId(row.weightCol) : '';
        return {
          rowCols: normalizedRowCols,
          colCols: normalizedColCols,
          weightCol: weight ? String(weight) : '',
        };
      })
      .filter(Boolean);

    const multiState = (Array.isArray(multiQuestionRows) ? multiQuestionRows : [])
      .map((row) => {
        const rawQuestions = Array.isArray(row.questions) ? row.questions : [];
        const normalizedQuestions = Array.from(
          new Set(
            rawQuestions
              .map((val) => toId(val))
              .filter((val) => val != null && String(val).trim() !== '')
              .map((val) => String(val))
          )
        );
        if (!normalizedQuestions.length) return null;

        const weight = row.weightCol ? toId(row.weightCol) : '';
        const likertEligible = areAllLikertQuestions(normalizedQuestions);
        const rawAggregation = Array.isArray(row.aggregation)
          ? row.aggregation
          : (row.aggregation ? [row.aggregation] : []);
        const normalizedAggregation = likertEligible
          ? Array.from(
              new Set(
                rawAggregation
                  .map((val) => String(val))
                  .filter((val) => LIKERT_CALC_VALUE_SET.has(val))
              )
            )
          : [];

        return {
          questions: normalizedQuestions,
          weightCol: weight ? String(weight) : '',
          aggregation: normalizedAggregation,
        };
      })
      .filter(Boolean);

    const expandedPivots = pivotState.map((row) => ({
      rowCols: row.rowCols,
      colCols: row.colCols,
      weight: row.weightCol || '',
    }));

    const expandedMultiQuestions = multiState.map((row) => {
      const payload = {
        questions: row.questions,
        weight: row.weightCol || '',
      };
      if (Array.isArray(row.aggregation) && row.aggregation.length) {
        payload.calculations = row.aggregation;
        if (row.aggregation.length === 1) {
          payload.calculation = row.aggregation[0];
        }
      }
      return payload;
    });

    const undupeStateRaw = Array.isArray(undupeQuestions) ? undupeQuestions : [];
    const expandedUndupeQuestions = undupeApiPayload;

    return {
      pivotState,
      multiState,
      expandedPivots,
      expandedMultiQuestions,
      undupeStateRaw,
      expandedUndupeQuestions,
    };
  }, [pivotRows, multiQuestionRows, toId, areAllLikertQuestions, undupeQuestions, undupeApiPayload]);

  const filterGroupsForApi = React.useMemo(() => {
    const groupsFromPayload =
      filterGroupPayload && typeof filterGroupPayload === 'object'
        ? filterGroupPayload.groups
        : null;
    if (Array.isArray(groupsFromPayload) && groupsFromPayload.length) {
      return groupsFromPayload;
    }
    if (Array.isArray(filters) && filters.length) {
      return [{ id: 'active', name: 'Aktif Grup', filters, weights: [] }];
    }
    return [];
  }, [filterGroupPayload, filters]);

  useEffect(() => {
    if (typeof onNormalizedQueryChange !== 'function') return;
    const normalizedState = buildNormalizedQueryState();
    const inputsPayload = {
      projectID: projectID || '',
      rows: normalizedState?.expandedPivots || [],
      column_types: columnMetadataForApi?.columnTypesCompact || [],
      id_to_header: idToHeaderMap || {},
      to_header: idToHeaderMap || {},
      multiquestion: normalizedState?.expandedMultiQuestions || [],
      multiquestions: normalizedState?.expandedMultiQuestions || [],
      filterGroups: filterGroupsForApi || [],
      likertGroups: columnMetadataForApi?.likertGroups || [],
      undupeQuestions: normalizedState?.expandedUndupeQuestions || [],
    };
    const serialized = JSON.stringify(inputsPayload);
    if (normalizedInputsRef.current === serialized) return;
    normalizedInputsRef.current = serialized;
    onNormalizedQueryChange({
      inputs: inputsPayload,
      columnTypes: columnMetadataForApi?.columnTypesCompact || [],
      likertGroups: columnMetadataForApi?.likertGroups || [],
      likertQuestions: columnMetadataForApi?.likertQuestions || [],
      filterGroups: filterGroupsForApi || [],
      idToHeader: idToHeaderMap || {},
      normalizedState,
    });
  }, [
    onNormalizedQueryChange,
    buildNormalizedQueryState,
    columnMetadataForApi,
    filterGroupsForApi,
    idToHeaderMap,
    projectID,
  ]);




  const executePivotQuery = async (mode = 'append', extraPayload = {}) => {
    const {
      pivotState,
      multiState,
      expandedPivots,
      expandedMultiQuestions,
      undupeStateRaw,
      expandedUndupeQuestions,
    } = buildNormalizedQueryState();
    if (!expandedPivots.length && !expandedMultiQuestions.length) {
      alert('En az bir pivot satırı veya multi soru seçmelisiniz.');
      return;
    }

    await persistQueryState(pivotState, multiState, undupeStateRaw);

    const formData = new FormData();
    if (projectID) formData.append('projectID', projectID);
    if (currentUserEmail) formData.append('useremail', currentUserEmail);
    let aiPromptValue = '';
    if (extraPayload && typeof extraPayload === 'object') {
      const promptInput =
        typeof extraPayload.user_promt === 'string'
          ? extraPayload.user_promt
          : (typeof extraPayload.userPrompt === 'string' ? extraPayload.userPrompt : '');
      const trimmedPrompt = typeof promptInput === 'string' ? promptInput.trim() : '';
      if (trimmedPrompt) {
        formData.append('user_promt', trimmedPrompt);
        aiPromptValue = trimmedPrompt;
      }
    }
    if (aiPromptValue && slimMainQuestionsPayload) {
      try {
        formData.append('mainQuestionsPayload', JSON.stringify(slimMainQuestionsPayload));
      } catch (err) {
        console.warn('mainQuestionsPayload serialize error:', err);
      }
    }
    const columnTypesForApi = columnMetadataForApi?.columnTypesCompact || [];
    const likertQuestions = columnMetadataForApi?.likertQuestions || [];
    const likertGroups = columnMetadataForApi?.likertGroups || [];

    formData.append('columnTypes', JSON.stringify(columnTypesForApi));
    formData.append('likertQuestions', JSON.stringify(likertQuestions));
    formData.append('likertGroups', JSON.stringify(likertGroups));
    formData.append('filterGroups', JSON.stringify(filterGroupsForApi));
    formData.append('pivots', JSON.stringify(expandedPivots));
    formData.append('multiquestion', JSON.stringify(expandedMultiQuestions));
    formData.append(
      'undupeQuestions',
      JSON.stringify(Array.isArray(expandedUndupeQuestions) ? expandedUndupeQuestions : [])
    );

    const progressLabel = mode === 'refresh' ? 'Tablolar yenileniyor…' : 'Tablolar oluşturuluyor…';
    setPersisting(true);
    setPersistText(progressLabel);

    try {
      const res = await fetch('https://reporting-create-tables-795950092325.europe-west3.run.app', { method: 'POST', body: formData });
      let payload = null;
      try {
        const responseText = await res.text();
        if (responseText && responseText.trim()) {
          payload = JSON.parse(responseText);
        }
      } catch (parseError) {
        console.warn('Pivot API yanıtı JSON parse edilemedi:', parseError);
      }

      if (!res.ok) {
        const errMessage =
          (payload && (payload.error || payload.message)) ||
          res.statusText ||
          `API ${res.status}`;
        throw new Error(errMessage);
      }

      console.log('payload', payload);

      const newTables = [];
      const pushTableFromShape = (tableShape, meta = {}) => {
        if (!tableShape) return;

        const title = tableShape.title || tableShape.name || 'Pivot Table';
        const colsArr = tableShape.columns || tableShape.headers || [];
        const rowsArr = tableShape.rows || [];

        const columns = Array.isArray(colsArr)
          ? colsArr.map((h) => (typeof h === 'string'
              ? { field: h, headerName: h, width: Math.max(120, h.length * 10) }
              : {
                  field: h.field || h.header || h.headerName,
                  headerName: h.header || h.headerName || h.field,
                  width: Math.max(120, String(h.header || h.headerName || h.field).length * 10),
                }
            ))
          : [];

        const rows = Array.isArray(rowsArr)
          ? rowsArr.map((r, i) => ({ id: r.id ?? i + 1, ...r }))
          : [];

        if (!columns.length || !rows.length) return;

        const lettersMap = tableShape.lettersMap || (tableShape.significance && tableShape.significance.lettersMap) || null;
        const comparisonGroups = tableShape.comparisonGroups || (tableShape.significance && tableShape.significance.comparisonGroups) || '';
        const significance = tableShape.significance || null;

        const sourceId = tableShape.id ?? tableShape.tableId ?? null;
        const groupIdRaw = meta.groupId ?? tableShape.groupId ?? tableShape.group_id ?? null;
        const groupNameRaw = meta.groupName ?? tableShape.groupName ?? tableShape.group_name ?? null;
        const filtersAppliedRaw = Array.isArray(meta.filtersApplied)
          ? meta.filtersApplied
          : (Array.isArray(meta.filters)
              ? meta.filters
              : (Array.isArray(tableShape.filtersApplied)
                  ? tableShape.filtersApplied
                  : (Array.isArray(tableShape.filters) ? tableShape.filters : [])));
        const groupOrder = typeof meta.groupOrder === 'number'
          ? meta.groupOrder
          : (typeof tableShape.groupOrder === 'number'
              ? tableShape.groupOrder
              : (typeof tableShape.group_index === 'number' ? tableShape.group_index : undefined));
        const isBaseGroup = Boolean(meta.isBaseGroup ?? tableShape.isBaseGroup);

        const resolvedGroupId = groupIdRaw != null
          ? String(groupIdRaw)
          : (isBaseGroup ? BASE_GROUP_ID : UNGROUPED_GROUP_ID);

        const resolvedGroupName = groupNameRaw
          || (resolvedGroupId === BASE_GROUP_ID ? DEFAULT_BASE_GROUP_NAME : null);
        const normalizedFilters = Array.isArray(filtersAppliedRaw) ? filtersAppliedRaw : [];

        const displayGroupName = (
          resolvedGroupName
          || (meta.groupName ? String(meta.groupName) : null)
          || (resolvedGroupId === BASE_GROUP_ID
                ? DEFAULT_BASE_GROUP_NAME
                : (meta.groupId ? `Grup ${String(meta.groupId).replace(/^grp[_-]?/, '')}` : DEFAULT_OTHER_GROUP_NAME)
             )
        );

        newTables.push({
          id: `tbl_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
          sourceTableId: sourceId ? String(sourceId) : null,
          title,
          columns,
          rows,
          lettersMap,
          comparisonGroups,
          significance,
          groupId: resolvedGroupId,
          groupName: displayGroupName,
          groupFilters: normalizedFilters,
          filtersApplied: normalizedFilters,
          isBaseGroup: resolvedGroupId === BASE_GROUP_ID || isBaseGroup,
          groupOrder,
          generatedByQuery: true,
          querySource: 'pivotBuilder',
          queryType: tableShape.queryType || meta.queryType || null,
          _groupMeta: {
            id: resolvedGroupId,
            name: displayGroupName,
            order: groupOrder,
            isBase: resolvedGroupId === BASE_GROUP_ID || isBaseGroup,
            filtersApplied: normalizedFilters,
          },
        });
      };

      const hasGroupedStructure = Array.isArray(payload?.baseTables) || Array.isArray(payload?.groups);

      if (hasGroupedStructure) {
        (Array.isArray(payload?.baseTables) ? payload.baseTables : []).forEach((tbl, idx) => {
          pushTableFromShape(tbl, {
            groupId: BASE_GROUP_ID,
            groupName: DEFAULT_BASE_GROUP_NAME,
            filtersApplied: [],
            isBaseGroup: true,
            groupOrder: idx,
          });
        });

        const groupsArr = Array.isArray(payload?.groups) ? payload.groups : [];
        groupsArr.forEach((grp, grpIndex) => {
          const groupId = grp?.id != null && String(grp.id).trim()
            ? String(grp.id)
            : `grp_${Date.now()}_${grpIndex}`;
          const groupName = grp?.name || `Filtre Grubu ${grpIndex + 1}`;

          let groupFilters = [];
          if (Array.isArray(grp?.filtersApplied)) {
            groupFilters = grp.filtersApplied;
          } else if (grp?.filtersApplied && typeof grp.filtersApplied === 'object') {
            groupFilters = [grp.filtersApplied];
          } else if (Array.isArray(grp?.filters)) {
            groupFilters = grp.filters;
          } else if (grp?.filters && typeof grp.filters === 'object') {
            groupFilters = [grp.filters];
          }

          let tablesArr = [];
          if (Array.isArray(grp?.tables)) {
            tablesArr = grp.tables;
          } else if (grp?.table) {
            tablesArr = [grp.table];
          } else if (grp?.tables && typeof grp.tables === 'object') {
            try {
              tablesArr = Object.values(grp.tables);
            } catch (_) {
              tablesArr = [];
            }
          }

          tablesArr.forEach((tbl) => {
            pushTableFromShape(tbl, {
              groupId,
              groupName,
              filtersApplied: groupFilters,
              isBaseGroup: false,
              groupOrder: grpIndex + 1,
            });
          });
        });

        try {
          const expectedGroupTableCount = groupsArr.reduce((acc, g) => {
            if (Array.isArray(g?.tables)) return acc + g.tables.length;
            if (g?.table) return acc + 1;
            if (g?.tables && typeof g.tables === 'object') {
              try { return acc + Object.values(g.tables).length; } catch { return acc; }
            }
            return acc;
          }, 0);
          const alreadyAddedFromGroups = newTables.filter(t => t.groupId && t.groupId !== BASE_GROUP_ID).length;

          if (alreadyAddedFromGroups < expectedGroupTableCount) {
            let autoIdx = 0;
            groupsArr.forEach((g, gIdx) => {
              let tArr = [];
              if (Array.isArray(g?.tables)) tArr = g.tables;
              else if (g?.table) tArr = [g.table];
              else if (g?.tables && typeof g.tables === 'object') {
                try { tArr = Object.values(g.tables); } catch { tArr = []; }
              }
              tArr.forEach((tbl) => {
                const fpTitle = String(tbl?.title || tbl?.name || '').trim();
                const exists = newTables.some(nt => nt.title === fpTitle && nt.groupId && nt.groupId !== BASE_GROUP_ID);
                if (!exists) {
                  const autoGroupId = `grp_auto_${Date.now()}_${gIdx}_${autoIdx++}`;
                  const autoGroupName = g?.name ? `${String(g.name)} (kopya)` : `Grup ${gIdx + 1}`;
                  let groupFilters = [];
                  if (Array.isArray(g?.filtersApplied)) groupFilters = g.filtersApplied;
                  else if (Array.isArray(g?.filters)) groupFilters = g.filters;
                  pushTableFromShape(tbl, {
                    groupId: autoGroupId,
                    groupName: autoGroupName,
                    filtersApplied: groupFilters,
                    isBaseGroup: false,
                    groupOrder: (
                      typeof g?.order === 'number'
                        ? g.order
                        : typeof gIdx === 'number'
                          ? gIdx + 1
                          : undefined
                    ),
                  });
                }
              });
            });
          }
        } catch (err) {
          console.warn('Gruplar yeniden eklenirken hata:', err);
        }
      }

      if (!newTables.length) {
        if (Array.isArray(payload?.tables)) {
          payload.tables.forEach((tbl, idx) => {
            pushTableFromShape(tbl, {
              groupId: UNGROUPED_GROUP_ID,
              groupName: DEFAULT_OTHER_GROUP_NAME,
              filtersApplied: [],
              isBaseGroup: false,
              groupOrder: idx,
            });
          });
        } else if (payload?.table) {
          pushTableFromShape(payload.table, {
            groupId: UNGROUPED_GROUP_ID,
            groupName: DEFAULT_OTHER_GROUP_NAME,
            filtersApplied: [],
            isBaseGroup: false,
          });
        } else if (Array.isArray(payload)) {
          payload.forEach((tbl, idx) => {
            pushTableFromShape(tbl, {
              groupId: UNGROUPED_GROUP_ID,
              groupName: DEFAULT_OTHER_GROUP_NAME,
              filtersApplied: [],
              isBaseGroup: false,
              groupOrder: idx,
            });
          });
        } else if (payload?.columns && payload?.rows) {
          pushTableFromShape(payload, {
            groupId: UNGROUPED_GROUP_ID,
            groupName: DEFAULT_OTHER_GROUP_NAME,
            filtersApplied: [],
            isBaseGroup: false,
          });
        }
      }

      let tablesWithQueryMeta = newTables.map((tbl) => ({
        ...tbl,
        generatedByQuery: tbl.generatedByQuery ?? true,
        querySource: tbl.querySource ?? 'pivotBuilder',
      }));
      let tablesLoadedFromLatestRun = false;

      if (!tablesWithQueryMeta.length) {
        const dbInstance = getDB();
        if (dbInstance && projectID) {
          try {
            setPersistText("Firestore'dan tablolar getiriliyor…");
            const latestTables = await loadTablesFromLatestRun(dbInstance, projectID);
            if (Array.isArray(latestTables) && latestTables.length) {
              tablesWithQueryMeta = latestTables;
              tablesLoadedFromLatestRun = true;
            }
          } catch (fireErr) {
            console.warn("Firestore'dan tablo okunamadı:", fireErr);
          }
        }
      }

      if (!tablesWithQueryMeta.length) {
        setPersistText('');
        alert('Sorgu gönderildi ancak yeni tablo verisi alınamadı. Biraz sonra tekrar deneyin.');
        return;
      }

      let tablesToPersist = tablesWithQueryMeta;
      let tablesToApply = [];
      let tablesToDelete = [];
      let successMessage = tablesLoadedFromLatestRun
        ? "Tablolar Firestore'dan güncellendi."
        : 'Tablolar güncellendi.';

      if (mode === 'refresh') {
        const existingGenerated = tableCards.filter(isQueryGeneratedTable);
        tablesToDelete = existingGenerated;
        const remainingTables = tableCards.filter((tbl) => !isQueryGeneratedTable(tbl));
        tablesToApply = [...tablesWithQueryMeta, ...remainingTables];
        applyTablesChange(tablesToApply);
        setRenderLimit(maxTablesPerSheet);
        setExpanded(new Set());
      } else {
        const existingGenerated = tableCards.filter(isQueryGeneratedTable);
        const manualTables = tableCards.filter((tbl) => !isQueryGeneratedTable(tbl));

        const existingBySource = new Map();
        const existingByFallback = new Map();
        existingGenerated.forEach((tbl) => {
          const srcKey = getGeneratedTableSourceKey(tbl);
          if (srcKey && !existingBySource.has(srcKey)) {
            existingBySource.set(srcKey, tbl);
          }
          const fallbackKey = getGeneratedTableFallbackKey(tbl);
          if (fallbackKey && !existingByFallback.has(fallbackKey)) {
            existingByFallback.set(fallbackKey, tbl);
          }
        });

        const usedExistingIds = new Set();
        let addedCount = 0;
        let updatedCount = 0;

        const mergedTables = tablesWithQueryMeta.map((tbl) => {
          const srcKey = getGeneratedTableSourceKey(tbl);
          let match = srcKey ? existingBySource.get(srcKey) : null;

          if (!match) {
            const fallbackKeyIncoming = getGeneratedTableFallbackKey(tbl);
            if (fallbackKeyIncoming) {
              match = existingByFallback.get(fallbackKeyIncoming);
            }
          }

          if (match && !usedExistingIds.has(match.id)) {
            usedExistingIds.add(match.id);
            const matchSourceKey = getGeneratedTableSourceKey(match);
            if (matchSourceKey) {
              existingBySource.delete(matchSourceKey);
            }
            const matchFallbackKey = getGeneratedTableFallbackKey(match);
            if (matchFallbackKey) {
              existingByFallback.delete(matchFallbackKey);
            }
            updatedCount += 1;
            return {
              ...match,
              ...tbl,
              id: match.id,
            };
          }

          addedCount += 1;
          return tbl;
        });

        tablesToPersist = mergedTables;
        tablesToApply = [...mergedTables, ...manualTables];
        tablesToDelete = existingGenerated.filter((tbl) => !usedExistingIds.has(tbl.id));
        applyTablesChange(tablesToApply);

        const summaryParts = [];
        if (updatedCount > 0) summaryParts.push(`${updatedCount} tablo güncellendi`);
        if (addedCount > 0) summaryParts.push(`${addedCount} yeni tablo eklendi`);
        if (tablesToDelete.length > 0) summaryParts.push(`${tablesToDelete.length} tablo silindi`);
        if (summaryParts.length) {
          successMessage = `${summaryParts.join(', ')}.`;
        }
      }

      if (mode === 'refresh') {
        successMessage = tablesLoadedFromLatestRun
          ? "Tablolar Firestore'dan güncellendi."
          : 'Tablolar güncellendi.';
      }

      if (!tablesLoadedFromLatestRun && hasFirebase && tablesToDelete.length) {
        setPersistText('Eski tablolar temizleniyor…');
        for (const tbl of tablesToDelete) {
          try {
            await deleteTableFromFirebase(tbl.id);
          } catch (err) {
            console.warn('Eski tablo silinemedi:', err);
          }
        }
      }

      if (!tablesLoadedFromLatestRun && hasFirebase) {
        if (tablesToPersist.length) {
          setPersistText(`${tablesToPersist.length} tablo kaydediliyor…`);
          await persistChunkToFirebase(tablesToPersist);
        } else {
          setPersistText('');
        }
      } else if (!tablesLoadedFromLatestRun && tablesToPersist.length) {
        setPersistText('Bağlantı bekleniyor…');
        pendingChunksRef.current.push(tablesToPersist);
      } else {
        setPersistText('');
      }

      setPersistText('');
      alert(successMessage);
    } catch (e) {
      console.error(mode === 'refresh' ? 'Tablo yenileme hatası' : 'Tablo API hatası', e);
      const errorMessage = mode === 'refresh'
        ? 'Tablolar güncellenirken hata oluştu.'
        : 'Tablo oluşturma sırasında hata oluştu.';
      alert(errorMessage);
    } finally {
      setPersisting(false);
      setPersistText('');
    }
  };

  const runPivotAndAddTables = async () => {
    await executePivotQuery('append');
  };

  const handleOpenAiDialog = () => {
    setAiPrompt('');
    setAiPromptError('');
    setAiDialogOpen(true);
  };

  const handleCloseAiDialog = () => {
    setAiDialogOpen(false);
    setAiPrompt('');
    setAiPromptError('');
  };

  const handleAiPromptChange = (event) => {
    const nextValue = event?.target?.value ?? '';
    if (aiPromptError) {
      setAiPromptError('');
    }
    setAiPrompt(nextValue);
  };

  const handleAiTableCreation = async () => {
    const trimmedPrompt = (aiPrompt || '').trim();
    if (!trimmedPrompt) {
      setAiPromptError('Lütfen bir metin girin.');
      return;
    }
    handleCloseAiDialog();
    await executePivotQuery('append', { userPrompt: trimmedPrompt });
  };

  // Tek noktadan güncelle + hafif kaydet (savedTables)
  const applyTablesChange = (next) => {
    onChange && onChange(next);
  };

  // Render yükünü sınırlamak için:
  const [renderLimit, setRenderLimit] = useState(maxTablesPerSheet);
  const [expanded, setExpanded] = useState(() => new Set());
  const isExpanded = (id) => expanded.has(id);
  const toggleExpand = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // ---- Firestore yardımcıları ----
  // --- Boyut sınırı ve sayfalama ---
  const MAX_DOC_BYTES = 900 * 1024; // güvenli tampon
  const PAGE_ROWS_DEFAULT = 200;     // tahmini parça
  const jsonSize = (obj) => new Blob([JSON.stringify(obj)]).size;

  // --- DB resolver: prefer prop `db`, else default app's Firestore if available
  const getDB = () => {
    if (db) return db;
    try {
      if (getApps().length > 0) {
        return getFirestore(getApp());
      }
    } catch {}
    return null;
  };

  // --- Resolve persist context with localStorage fallback
  const resolvedCtx = useMemo(() => {
    const base = persistCtx || {};
    if (base.organisation) return base;
    try {
      const org = window.localStorage.getItem('organisation') || window.localStorage.getItem('org');
      const projectID = window.localStorage.getItem('projectID') || window.localStorage.getItem('currentProjectID');
      const analysisID = window.localStorage.getItem('analysisID') || window.localStorage.getItem('currentAnalysisID');
      if (org) return { organisation: org, projectID: projectID || undefined, analysisID: analysisID || undefined };
    } catch {}
    return base;
  }, [persistCtx]);

  // --- Firebase presence: use getDB() and resolvedCtx
  const hasFirebase = !!getDB() && !!resolvedCtx && !!resolvedCtx.organisation;
  const getDebugPath = () => {
    const { organisation, projectID, analysisID } = resolvedCtx || {};
    if (!organisation) return '(no organisation)';
    return analysisID
      ? `organisations/${organisation}/analyses/${analysisID}/${TABLE_COLLECTION}`
      : `organisations/${organisation}/projects/${projectID}/${TABLE_COLLECTION}`;
  };

  // --- Load pivot options from Firestore (graphDatasets -> analysis -> project.rawData) ---
  React.useEffect(() => {
    (async () => {
      try {
        const _db = getDB();
        const { organisation, projectID, analysisID } = resolvedCtx || {};
        if (!_db || !organisation || !projectID) return;
        const res = await fetchPivotOptions({ db: _db, organisation, projectID, analysisID });
        const fetchedColTypes = Array.isArray(res.colTypes) ? res.colTypes : [];
        setDbColTypes(fetchedColTypes);
        // Only use main questions (displayCols) as pivot options
        setDbLeafOptions(Array.isArray(res.displayCols) ? res.displayCols : []);
        setDbRowGroupOptions(Array.isArray(res.rowGroupOptions) ? res.rowGroupOptions : []);
      } catch (e) {
        console.warn('Pivot options fetch failed:', e);
      }
    })();
  }, [resolvedCtx]);

  // --- Pending chunks queue for auto-persist ---
  const pendingChunksRef = React.useRef([]);
  const lastPersistedQueryStateRef = React.useRef('');
  const didLoadQueryStateRef = React.useRef(false);
  React.useEffect(() => {
    (async () => {
      if (!hasFirebase || pendingChunksRef.current.length === 0) return;
      const chunks = [...pendingChunksRef.current];
      pendingChunksRef.current = [];
      try {
        setPersisting(true);
        setPersistText('Kuyruk yazılıyor…');
        console.debug('[TableCard] Flushing pending table datasets:', chunks.length);
        await persistChunkToFirebase(chunks.flat());
        setPersistText('');
        setPersisting(false);
      } catch (e) {
        console.warn('[TableCard] Pending flush failed, re‑queueing', e);
        pendingChunksRef.current = chunks;
        setPersisting(false);
      }
    })();
  }, [hasFirebase]);

  // --- Firestore helpers updated to use getDB() and resolvedCtx
  const getTablesCollectionRef = () => {
    const _db = getDB();
    if (!_db) return null;
    const { organisation, projectID, analysisID } = resolvedCtx;
    if (!organisation) return null;
    if (analysisID) {
      // organisations/{org}/analyses/{analysisID}/tableDatasets
      return collection(_db, 'organisations', organisation, 'analyses', analysisID, TABLE_COLLECTION);
    }
    return collection(_db, 'organisations', organisation, 'projects', projectID, TABLE_COLLECTION);
  };

  const getTableBuilderStateDocRef = React.useCallback(() => {
    const _db = getDB();
    if (!_db) return null;
    const { organisation, projectID, analysisID } = resolvedCtx || {};
    if (!organisation) return null;
    if (analysisID) {
      return doc(
        _db,
        'organisations',
        organisation,
        'analyses',
        analysisID,
        TABLE_STATE_COLLECTION,
        TABLE_STATE_DOC
      );
    }
    if (!projectID) return null;
    return doc(
      _db,
      'organisations',
      organisation,
      'projects',
      projectID,
      TABLE_STATE_COLLECTION,
      TABLE_STATE_DOC
    );
  }, [resolvedCtx]);

  React.useEffect(() => {
    if (!hasFirebase || didLoadQueryStateRef.current) return;
    let cancelled = false;
    (async () => {
      const docRef = getTableBuilderStateDocRef();
      if (!docRef) {
        didLoadQueryStateRef.current = true;
        return;
      }
      try {
        const snap = await getDoc(docRef);
        if (!snap.exists()) {
          return;
        }
        const data = snap.data() || {};

        const sanitizePivotRow = (row) => {
          if (!row || typeof row !== 'object') return null;
          const rowCols = Array.isArray(row.rowCols)
            ? row.rowCols.map((val) => String(val)).filter((val) => val.trim() !== '')
            : [];
          if (!rowCols.length) return null;
          const colCols = Array.isArray(row.colCols)
            ? Array.from(new Set(row.colCols.map((val) => String(val)).filter((val) => val.trim() !== '')))
            : [];
          const weightCol = row.weightCol != null ? String(row.weightCol) : '';
          return {
            rowCols,
            colCols,
            weightCol,
          };
        };

        const sanitizeMultiRow = (row) => {
          if (!row || typeof row !== 'object') return null;
          const questions = Array.isArray(row.questions)
            ? row.questions.map((val) => String(val)).filter((val) => val.trim() !== '')
            : [];
          if (!questions.length) return null;
          const weightCol = row.weightCol != null ? String(row.weightCol) : '';
          const aggregation = Array.isArray(row.aggregation)
            ? row.aggregation
                .map((val) => String(val))
                .filter((val) => LIKERT_CALC_VALUE_SET.has(val))
            : [];
          return {
            questions,
            weightCol,
            aggregation,
          };
        };

        const sanitizeUndupeRow = (row, idx) => {
          if (!row || typeof row !== 'object') return null;
          const baseQuestionId = row.baseQuestionId != null ? String(row.baseQuestionId).trim() : '';
          const newQuestionLabel = row.newQuestionLabel != null ? String(row.newQuestionLabel).trim() : '';
          const hiddenOptionValues = Array.isArray(row.hiddenOptionValues)
            ? Array.from(
                new Set(
                  row.hiddenOptionValues
                    .map((val) => String(val).trim())
                    .filter((val) => val !== '')
                )
              )
            : [];

          const mergesRaw = Array.isArray(row.merges) ? row.merges : [];
          const sanitizedMerges = mergesRaw
            .map((merge, mergeIdx) => {
              if (!merge || typeof merge !== 'object') return null;
              const mergeId =
                merge.id != null && String(merge.id).trim() !== ''
                  ? String(merge.id).trim()
                  : `${row.id || 'undupe'}_merge_${mergeIdx}`;
              const mergeOptionValues = Array.isArray(merge.mergeOptionValues)
                ? Array.from(
                    new Set(
                      merge.mergeOptionValues
                        .map((val) => String(val).trim())
                        .filter((val) => val !== '')
                    )
                  )
                : [];
              const newOptionLabel =
                merge.newOptionLabel != null ? String(merge.newOptionLabel).trim() : '';
              return {
                id: mergeId,
                mergeOptionValues,
                newOptionLabel,
              };
            })
            .filter(Boolean);

          // Legacy support: older schema stored single merge fields at top-level
          if (
            (!sanitizedMerges.length) &&
            Array.isArray(row.mergeOptionValues) &&
            row.mergeOptionValues.length
          ) {
            const legacyValues = Array.from(
              new Set(
                row.mergeOptionValues
                  .map((val) => String(val).trim())
                  .filter((val) => val !== '')
              )
            );
            if (legacyValues.length) {
              sanitizedMerges.push({
                id: `${row.id || 'undupe'}_merge_0`,
                mergeOptionValues: legacyValues,
                newOptionLabel: row.newOptionLabel != null ? String(row.newOptionLabel).trim() : '',
              });
            }
          }

          const id =
            row.id != null && String(row.id).trim() !== ''
              ? String(row.id).trim()
              : `${generateUndupeLocalId()}_${idx}`;
          return {
            id,
            baseQuestionId,
            newQuestionLabel,
            hiddenOptionValues,
            merges: sanitizedMerges,
          };
        };

        const pivotRowsFromDb = Array.isArray(data.pivotRows) ? data.pivotRows : [];
        const multiRowsFromDb = Array.isArray(data.multiQuestionRows) ? data.multiQuestionRows : [];
        const undupeRowsFromDb = Array.isArray(data.undupeQuestions) ? data.undupeQuestions : [];

        const sanitizedPivotRows = pivotRowsFromDb
          .map(sanitizePivotRow)
          .filter(Boolean);
        const sanitizedMultiRows = multiRowsFromDb
          .map(sanitizeMultiRow)
          .filter(Boolean);
        const sanitizedUndupeRows = undupeRowsFromDb
          .map((row, idx) => sanitizeUndupeRow(row, idx))
          .filter(Boolean);

        if (!cancelled) {
          if (sanitizedPivotRows.length) {
            setPivotRows(sanitizedPivotRows);
          }
          if (sanitizedMultiRows.length) {
            setMultiQuestionRows(sanitizedMultiRows);
          }
          setUndupeQuestions(sanitizedUndupeRows);
          const signature = JSON.stringify({ p: sanitizedPivotRows, m: sanitizedMultiRows, u: sanitizedUndupeRows });
          lastPersistedQueryStateRef.current = signature;
        }
      } catch (err) {
        console.warn('Kaydedilmiş pivot sorguları yüklenemedi:', err);
      } finally {
        if (!cancelled) {
          didLoadQueryStateRef.current = true;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasFirebase, getTableBuilderStateDocRef]);

  const persistQueryState = React.useCallback(
    async (pivotState, multiState, undupeState) => {
      if (!hasFirebase) return;
      const docRef = getTableBuilderStateDocRef();
      if (!docRef) return;
      const safePivot = Array.isArray(pivotState) ? pivotState : [];
      const safeMulti = Array.isArray(multiState) ? multiState : [];
      const safeUndupe = Array.isArray(undupeState) ? undupeState : [];
      const signature = JSON.stringify({ p: safePivot, m: safeMulti, u: safeUndupe });
      if (lastPersistedQueryStateRef.current === signature) return;
      const payload = {
        pivotRows: safePivot,
        multiQuestionRows: safeMulti,
        undupeQuestions: safeUndupe,
        updatedAt: serverTimestamp(),
      };
      if (currentUserEmail) {
        payload.updatedBy = currentUserEmail;
      }
      try {
        await setDoc(docRef, payload, { merge: true });
        lastPersistedQueryStateRef.current = signature;
      } catch (err) {
        console.warn('Pivot sorguları kaydedilemedi:', err);
      }
    },
    [hasFirebase, currentUserEmail, getTableBuilderStateDocRef]
  );

  const getTableDocRef = (tableId) => {
    const colRef = getTablesCollectionRef();
    if (!colRef) return null;
    return doc(colRef, tableId);
  };
  const getPagesCollectionRef = (tableId) => {
    const tblDoc = getTableDocRef(tableId);
    if (!tblDoc) return null;
    return collection(tblDoc, 'pages');
  };

  // --- Helper: Load only meta info for all tables (no rows)
  const loadMetaListFromFirebase = async () => {
    const colRef = getTablesCollectionRef();
    if (!colRef) return [];
    const snap = await getDocs(colRef);
    const tables = [];
    for (const d of snap.docs) {
      const meta = d.data();

      const parsedColumns = (() => {
        const raw = typeof meta.columnsJSON === 'string' ? meta.columnsJSON : '';
        if (raw) {
          try {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr)) {
              return arr.map((col, idx) => {
                const field = col?.field ?? col?.headerName ?? `col_${idx}`;
                const headerName = col?.headerName ?? col?.field ?? field;
                const next = {
                  field,
                  headerName,
                };
                if (typeof col?.width === 'number') {
                  next.width = col.width;
                }
                if (col?.type) {
                  next.type = col.type;
                }
                return next;
              });
            }
          } catch (err) {
            console.warn('columnsJSON parse failed', err);
          }
        }
        return Array.isArray(meta.columns) ? meta.columns : [];
      })();

      const parsedSignificance = (() => {
        const raw = typeof meta.significanceJSON === 'string' ? meta.significanceJSON : '';
        if (raw) {
          try {
            return JSON.parse(raw);
          } catch (err) {
            console.warn('significanceJSON parse failed', err);
          }
        }
        return meta.significance || null;
      })();

      tables.push({
        id: d.id,
        sheet: meta.sheet || null,
        title: meta.title || d.id,
        columns: parsedColumns,
        rows: null, // henüz yüklenmedi (lazy)
        _metaRowCount: meta.rowCount ?? 0,
        _persistedPageCount: typeof meta.pageCount === 'number' ? meta.pageCount : 0,
        lettersMap: meta.lettersMap || (parsedSignificance && parsedSignificance.lettersMap) || null,
        comparisonGroups: meta.comparisonGroups || (parsedSignificance && parsedSignificance.comparisonGroups) || '',
        significance: parsedSignificance,
        groupId: meta.groupId || null,
        groupName: meta.groupName || null,
        groupFilters: Array.isArray(meta.groupFilters) ? meta.groupFilters : [],
        filtersApplied: Array.isArray(meta.filtersApplied) ? meta.filtersApplied : (Array.isArray(meta.groupFilters) ? meta.groupFilters : []),
        isBaseGroup: !!meta.isBaseGroup,
        groupOrder: typeof meta.groupOrder === 'number' ? meta.groupOrder : undefined,
        sourceTableId: meta.sourceTableId || null,
        generatedByQuery: !!meta.generatedByQuery,
        querySource: meta.querySource || null,
        queryType: meta.queryType || null,
      });
    }
    return tables;
  };

  // --- Helper: Load rows for a single table (by id)
  const loadRowsForTable = async (tableId) => {
    const colRef = getTablesCollectionRef();
    if (!colRef) return [];
    const tableDoc = doc(colRef, tableId);
    const pagesRef = collection(tableDoc, 'pages');
    const pagesSnap = await getDocs(query(pagesRef, orderBy('index')));
    const pages = pagesSnap.docs.map(p => ({ id: p.id, ...p.data() }));
    return pages.flatMap(p => Array.isArray(p.rows) ? p.rows : []);
  };

  const persistChunkToFirebase = async (chunk) => {
    const colRef = getTablesCollectionRef();
    if (!colRef) return;

    const tables = Array.isArray(chunk) ? chunk.filter(Boolean) : [];
    if (!tables.length) return;

    const dbInstance = getDB();
    if (!dbInstance) return;

    let writesSincePause = 0;
    const pauseIfBusy = async (force = false) => {
      if (force || writesSincePause >= FIRESTORE_MAX_WRITES_BEFORE_PAUSE) {
        await new Promise((resolve) => setTimeout(resolve, FIRESTORE_WRITE_PAUSE_MS));
        writesSincePause = 0;
      }
    };

    const persistSingleTable = async (t) => {
      if (!t || !t.id) return;

      const tableId = t.id;
      const metaDocRef = getTableDocRef(tableId);
      const pagesColRef = getPagesCollectionRef(tableId);
      if (!metaDocRef || !pagesColRef) return;

      const columnsSlim = Array.isArray(t.columns)
        ? t.columns.map((col) => {
            const field = col?.field ?? col?.headerName ?? '';
            const headerName = col?.headerName ?? col?.field ?? '';
            const slim = {
              field,
              headerName,
            };
            if (typeof col?.width === 'number') {
              slim.width = col.width;
            }
            if (col?.type) {
              slim.type = col.type;
            }
            return slim;
          })
        : [];

      const significanceJSON = t.significance ? JSON.stringify(t.significance) : null;
      const columnsJSON = JSON.stringify(columnsSlim);
      const rows = Array.isArray(t.rows) ? t.rows : [];

      const pagePayloads = [];
      let pageIndex = 0;
      let start = 0;
      while (start < rows.length) {
        let end = Math.min(start + PAGE_ROWS_DEFAULT, rows.length);
        let slice = rows.slice(start, end);
        while (jsonSize({ rows: slice }) > MAX_DOC_BYTES && slice.length > 10) {
          end = start + Math.floor(slice.length * 0.7);
          slice = rows.slice(start, end);
        }
        pagePayloads.push({
          docId: `page_${String(pageIndex).padStart(4, '0')}`,
          index: pageIndex,
          rows: slice,
        });
        pageIndex += 1;
        start = end;
      }

      const hasPersistedPageCount = typeof t._persistedPageCount === 'number';
      const previousPageCount = hasPersistedPageCount ? t._persistedPageCount : 0;
      const newPageCount = pagePayloads.length;

      const metaPayload = {
        title: t.title,
        sheet: t.sheet || null,
        columnCount: columnsSlim.length,
        columnsJSON,
        rowCount: rows.length,
        pageCount: newPageCount,
        updatedAt: serverTimestamp(),
        version: 3,
        lettersMap: t.lettersMap || (t.significance && t.significance.lettersMap) || null,
        comparisonGroups: t.comparisonGroups || (t.significance && t.significance.comparisonGroups) || '',
        significanceJSON,
        groupId: t.groupId || null,
        groupName: t.groupName || null,
        groupFilters: Array.isArray(t.groupFilters) ? t.groupFilters : (Array.isArray(t.filtersApplied) ? t.filtersApplied : []),
        filtersApplied: Array.isArray(t.filtersApplied) ? t.filtersApplied : (Array.isArray(t.groupFilters) ? t.groupFilters : []),
        isBaseGroup: !!t.isBaseGroup,
        groupOrder: typeof t.groupOrder === 'number' ? t.groupOrder : null,
        sourceTableId: t.sourceTableId || null,
        generatedByQuery: !!t.generatedByQuery,
        querySource: t.querySource || null,
        queryType: t.queryType || null,
      };

      const metaDocPayload = {
        ...metaPayload,
        columns: deleteField(),
        significance: deleteField(),
        rows: deleteField(),
      };

      if (!hasPersistedPageCount) {
        const existingPagesSnap = await getDocs(pagesColRef);
        if (!existingPagesSnap.empty) {
          const docs = existingPagesSnap.docs;
          let idx = 0;
          while (idx < docs.length) {
            const batch = writeBatch(dbInstance);
            const slice = docs.slice(idx, idx + 450);
            slice.forEach((d) => batch.delete(d.ref));
            await batch.commit();
            writesSincePause += slice.length;
            await pauseIfBusy();
            idx += 450;
          }
        }
      }

      const createBatchHelper = () => {
        let batch = writeBatch(dbInstance);
        let opCount = 0;

        const commit = async (force = false) => {
          if (opCount === 0) return;
          await batch.commit();
          writesSincePause += opCount;
          await pauseIfBusy(force);
          batch = writeBatch(dbInstance);
          opCount = 0;
        };

        const ensureCapacity = async () => {
          if (opCount >= 450) {
            await commit();
          }
        };

        return {
          async set(ref, data, options) {
            if (options) {
              batch.set(ref, data, options);
            } else {
              batch.set(ref, data);
            }
            opCount += 1;
            await ensureCapacity();
          },
          async delete(ref) {
            batch.delete(ref);
            opCount += 1;
            await ensureCapacity();
          },
          async flush(force = false) {
            await commit(force);
          },
        };
      };

      const batchHelper = createBatchHelper();

      await batchHelper.set(metaDocRef, metaDocPayload, { merge: true });

      for (const payload of pagePayloads) {
        const pageDocRef = doc(pagesColRef, payload.docId);
        await batchHelper.set(pageDocRef, { index: payload.index, rows: payload.rows });
      }

      if (previousPageCount > newPageCount) {
        for (let idx = newPageCount; idx < previousPageCount; idx += 1) {
          const staleDocRef = doc(pagesColRef, `page_${String(idx).padStart(4, '0')}`);
          await batchHelper.delete(staleDocRef);
        }
      }

      await batchHelper.flush(true);

      t._persistedPageCount = newPageCount;
      t._metaRowCount = rows.length;
    };

    for (let i = 0; i < tables.length; i += 1) {
      await persistSingleTable(tables[i]);
      await pauseIfBusy(true);
    }
  };

  const deleteTableFromFirebase = async (tableId) => {
    const _db = getDB();
    if (!_db) return;
    const metaDocRef = getTableDocRef(tableId);
    const pagesColRef = getPagesCollectionRef(tableId);
    if (!metaDocRef || !pagesColRef) return;

    // Delete pages subcollection first
    const pagesSnap = await getDocs(pagesColRef);
    if (!pagesSnap.empty) {
      // Firestore batch limit is 500; chunk if needed
      const docs = pagesSnap.docs;
      let idx = 0;
      while (idx < docs.length) {
        const batch = writeBatch(_db);
        const chunk = docs.slice(idx, idx + 450);
        chunk.forEach(d => batch.delete(d.ref));
        await batch.commit();
        idx += 450;
      }
    }

    // Delete the meta document last
    await deleteDoc(metaDocRef);
  };

  const loadFromFirebase = async () => {
    const metas = await loadMetaListFromFirebase();
    // `onChange` ile sadece başlık/list verisini doldur (rows=null)
    onChange && onChange(metas.map(m => ({
      id: m.id,
      sheet: m.sheet,
      title: m.title,
      columns: m.columns,
      rows: null,
      lettersMap: m.lettersMap,
      comparisonGroups: m.comparisonGroups,
      significance: m.significance,
      generatedByQuery: m.generatedByQuery,
      querySource: m.querySource,
      queryType: m.queryType,
      sourceTableId: m.sourceTableId,
    })));
    return metas;
  };

  // --- Auto-load from Firebase on mount ---
  React.useEffect(() => {
    (async () => {
      if (!autoLoad || !hasFirebase) return;
      try {
        setImporting(true);
        setImportText('Firebase’den yükleniyor…');
        const loaded = await loadFromFirebase();
        if (loaded && loaded.length) {
            onChange && onChange(loaded);
            setRenderLimit(maxTablesPerSheet);
            setExpanded(new Set());
        }
      } catch (e) {
        console.warn('Firebase yükleme hatası', e);
      } finally {
        setImporting(false);
        setImportText('');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddTableFromFile = async (file) => {
    if (!file) return;
    try {
      const rows = await parseTableFile(file);
      const cols = buildColumnsFromRows(rows);
      const newCard = {
        id: `tbl_${Date.now()}`,
        title: file.name.replace(/\.(xlsx|xls|csv|txt)$/i, ''),
        columns: cols,
        rows: rows.map((r, i) => ({ id: r.id ?? i + 1, ...r }))
      };
      applyTablesChange([newCard, ...tableCards]);
      // İsteğe bağlı: import sonrası otomatik kaydet (yalnızca yeni tabloyu yaz)
      if (autoPersistOnImport) {
        const chunk = [newCard];
        if (hasFirebase) {
          setPersisting(true);
          setPersistText(`${chunk.length} tablo`);
          console.debug('[TableCard] Persisting imported table to', getDebugPath());
          await persistChunkToFirebase(chunk);
          setPersistText('');
          setPersisting(false);
        } else {
          setPersisting(true);
          setPersistText('Bağlantı bekleniyor…');
          console.warn('[TableCard] Firebase not ready, queueing chunk');
          pendingChunksRef.current.push(chunk);
        }
        if (onPersist) {
          try { await onPersist(chunk, { index: 1, total: 1 }); } catch (e) { console.warn('onPersist failed', e); }
        }
      }
    } catch (e) {
      console.warn('Tablo içe aktarılamadı', e);
      alert('Tablo içe aktarma başarısız. Lütfen CSV dosyası deneyin veya panodan yapıştırın.');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePasteCSV = async () => {
    const txt = prompt('CSV/TSV verisini buraya yapıştırın (ilk satır başlık olmalı):');
    if (!txt) return;
    try {
      const rows = parseCSVText(txt);
      const cols = buildColumnsFromRows(rows);
      const newCard = {
        id: `tbl_${Date.now()}`,
        title: `Pasted Table ${new Date().toLocaleString()}`,
        columns: cols,
        rows: rows.map((r, i) => ({ id: r.id ?? i + 1, ...r }))
      };
      applyTablesChange([newCard, ...tableCards]);
      // İsteğe bağlı: yapıştırma sonrası otomatik kaydet (yalnızca yeni tabloyu yaz)
      if (autoPersistOnImport) {
        const chunk = [newCard];
        if (hasFirebase) {
          setPersisting(true);
          setPersistText(`${chunk.length} tablo`);
          console.debug('[TableCard] Persisting pasted table to', getDebugPath());
          await persistChunkToFirebase(chunk);
          setPersistText('');
          setPersisting(false);
        } else {
          setPersisting(true);
          setPersistText('Bağlantı bekleniyor…');
          console.warn('[TableCard] Firebase not ready, queueing chunk');
          pendingChunksRef.current.push(chunk);
        }
        if (onPersist) {
          try { await onPersist(chunk, { index: 1, total: 1 }); } catch (e) { console.warn('onPersist failed', e); }
        }
      }
    } catch {
      alert('Yapıştırılan metin tabloya dönüştürülemedi.');
    }
  };

  const handleRename = (id) => {
    const title = prompt('Yeni tablo adı:');
    if (!title) return;
    applyTablesChange(tableCards.map(t => t.id === id ? { ...t, title } : t));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu tablo kartını silmek istiyor musunuz?')) return;

    // Optimistic UI update: remove from local state and collapse if expanded
    applyTablesChange(tableCards.filter(t => t.id !== id));
    setExpanded(prev => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });

    // Remove from Firebase if connected
    try {
      if (hasFirebase) {
        setPersisting(true);
        setPersistText('Tablo siliniyor…');
        await deleteTableFromFirebase(id);
        setPersistText('');
        setPersisting(false);
      }
    } catch (e) {
      console.warn('Firebase tekil silme hatası:', e);
      setPersisting(false);
      alert('Uyarı: Tablo yerelden silindi ancak veritabanından silinirken hata oluştu. Sayfayı yenileyip tekrar deneyin.');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Tüm tablo kartlarını silmek istiyor musunuz? Bu işlem geri alınamaz.')) return;
    applyTablesChange([]);
    if (hasFirebase) {
      try {
        const colRef = getTablesCollectionRef();
        const snap = await getDocs(colRef);
        const batch = writeBatch(getDB());
        for (const docSnap of snap.docs) {
          // delete meta doc
          batch.delete(docSnap.ref);
          // delete pages subcollection
          const pagesRef = collection(docSnap.ref, 'pages');
          const pagesSnap = await getDocs(pagesRef);
          pagesSnap.docs.forEach(p => batch.delete(p.ref));
        }
        await batch.commit();
      } catch (e) {
        console.warn('Firebase toplu silme hatası:', e);
      }
    }
  };

  

  const sidebarGroups = useMemo(() => {
    if (!Array.isArray(tableCards) || tableCards.length === 0) return [];
    const map = new Map();

    tableCards.forEach((tbl) => {
      const info = extractGroupInfoFromTable(tbl);
      const key = info.id || UNGROUPED_GROUP_ID;

      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name: info.name,
          filtersApplied: Array.isArray(info.filtersApplied) ? info.filtersApplied : [],
          isBaseGroup: !!info.isBaseGroup,
          order: typeof info.order === 'number'
            ? info.order
            : (info.isBaseGroup ? 0 : Number.MAX_SAFE_INTEGER),
          tables: [],
        });
      }

      const group = map.get(key);
      if (info.name && (!group.name || group.name === DEFAULT_OTHER_GROUP_NAME)) {
        group.name = info.name;
      }
      if (Array.isArray(info.filtersApplied) && info.filtersApplied.length && (!group.filtersApplied || group.filtersApplied.length === 0)) {
        group.filtersApplied = info.filtersApplied;
      }
      if (typeof info.order === 'number') {
        group.order = Math.min(group.order ?? info.order, info.order);
      }
      group.tables.push({ id: tbl.id, title: tbl.title, sheet: tbl.sheet || null });
    });

    return Array.from(map.values())
      .filter((g) => Array.isArray(g.tables) && g.tables.length > 0)
      .sort((a, b) => {
        const orderA = typeof a.order === 'number' ? a.order : (a.isBaseGroup ? -1 : Number.MAX_SAFE_INTEGER);
        const orderB = typeof b.order === 'number' ? b.order : (b.isBaseGroup ? -1 : Number.MAX_SAFE_INTEGER);
        if (orderA !== orderB) return orderA - orderB;
        if (a.isBaseGroup && !b.isBaseGroup) return -1;
        if (!a.isBaseGroup && b.isBaseGroup) return 1;
        return (a.name || '').localeCompare(b.name || '', 'tr', { sensitivity: 'base' });
      });
  }, [tableCards]);

  React.useEffect(() => {
    if (!sidebarGroups.length) {
      if (selectedGroupId) setSelectedGroupId('');
      if (openGroupId) setOpenGroupId('');
      return;
    }

    const selectedExists = sidebarGroups.some((g) => g.id === selectedGroupId);
    if (!selectedExists) {
      const first = sidebarGroups[0];
      setSelectedGroupId(first.id);
      if (!openGroupId) {
        setOpenGroupId(first.id);
      }
      return;
    }

    if (openGroupId && !sidebarGroups.some((g) => g.id === openGroupId)) {
      setOpenGroupId(selectedGroupId);
    }
  }, [sidebarGroups, selectedGroupId, openGroupId]);

  const visibleTables = useMemo(() => {
    if (!Array.isArray(tableCards) || tableCards.length === 0) return tableCards;
    const currentGroupId = selectedGroupId || (sidebarGroups[0]?.id ?? null);
    if (!currentGroupId) return tableCards;
    return tableCards.filter((tbl) => {
      const info = extractGroupInfoFromTable(tbl);
      return info.id === currentGroupId;
    });
  }, [tableCards, selectedGroupId, sidebarGroups]);

  const summarizeGroupFilters = React.useCallback((filters, isBaseGroup) => {
    if (isBaseGroup) return 'Baz veri';
    if (!Array.isArray(filters) || filters.length === 0) return 'Filtre uygulanmadı';

    const parts = [];
    for (let i = 0; i < filters.length; i += 1) {
      if (parts.length === 2) break;
      const f = filters[i] || {};
      const rawLabel = f.label ?? f.question ?? f.header ?? f.name ?? f.col ?? f.column ?? '';
      const label = rawLabel ? fmt(String(rawLabel)) : 'Soru';

      const valuesSource = Array.isArray(f.values)
        ? f.values
        : (Array.isArray(f.vals)
            ? f.vals
            : (Array.isArray(f.labels) ? f.labels : []));
      const normalizedValues = (valuesSource || []).map((val) => {
        if (typeof val === 'string') return val;
        if (val && typeof val === 'object') {
          return val.label || val.text || val.name || val.value || '';
        }
        return val;
      }).filter((v) => v != null && v !== '');

      const preview = normalizedValues.slice(0, 3).map((v) => String(v)).join(', ');
      const suffix = normalizedValues.length > 3 ? '…' : '';
      parts.push(`${label}: ${preview || '-'}${suffix}`.trim());
    }

    const extraCount = filters.length > 2 ? ` (+${filters.length - 2})` : '';
    return `${parts.join(' • ')}${extraCount}`.trim();
  }, [fmt]);

  const limitedTables = useMemo(() => visibleTables.slice(0, renderLimit), [visibleTables, renderLimit]);
  const selectedTables = useMemo(
    () => visibleTables.filter(t => expanded.has(t.id)),
    [visibleTables, expanded]
  );

  // Helper: Update a table's rows in state
  const setTableRows = (id, loadedRows) => {
    onChange && onChange(tableCards.map(t => t.id === id ? { ...t, rows: loadedRows } : t));
  };

  // --- Tablo satır anahtarı yardımcıları ---
  const inferRowKey = (tbl) => {
    if (!tbl) return '_row';
    if (tbl.rows && tbl.rows.length) {
      if ('_row' in tbl.rows[0]) return '_row';
      const keys = Object.keys(tbl.rows[0]).filter(k => k !== 'id');
      return keys[0] || '_row';
    }
    return '_row';
  };

  // Pivot option builders (moved to component scope)
  const currentTableForOptions = () => {
    // Prefer a table that actually has columns or rows
    const pick = (arr=[]) => arr.find(t => (Array.isArray(t.columns) && t.columns.length > 0) || (Array.isArray(t.rows) && t.rows.length > 0));
    return pick(selectedTables) || pick(visibleTables) || (selectedTables[0] || visibleTables[0] || null);
  };

  const allColumnOptions = useMemo(() => {
    const appendUndupe = (opts = []) => normalizeOptions([...(opts || []), ...undupeSelectOptions]);

    // 0) Prefer DB-provided options if available (now: only main questions)
    if (Array.isArray(dbLeafOptions) && dbLeafOptions.length) {
      return appendUndupe(dbLeafOptions);
    }

    // 1) Fallback to metadata supplied via props/local cache
    if (Array.isArray(fallbackColumnOptions) && fallbackColumnOptions.length) {
      return appendUndupe(fallbackColumnOptions);
    }

    // 2) Fallback to current table inference
    const t = currentTableForOptions();
    if (!t) return [];

    let cols = Array.isArray(t.columns) && t.columns.length
      ? t.columns
          .filter(c => (c?.field || c?.headerName) && (c?.field || c?.headerName) !== 'id')
          .map(c => ({ id: c.field || c.headerName, header: c.headerName || c.field }))
      : [];

    if ((!cols || cols.length === 0) && Array.isArray(t.rows) && t.rows.length > 0) {
      const rowKey = inferRowKey(t);
      const keys = Object.keys(t.rows[0] || {}).filter(k => k !== 'id' && k !== rowKey);
      cols = keys.map(k => ({ id: k, header: k }));
    }

    return appendUndupe(cols);
  }, [dbLeafOptions, fallbackColumnOptions, selectedTables, visibleTables, normalizeOptions, undupeSelectOptions]);

  const optionLabelById = useMemo(() => {
    const map = new Map();
    (allColumnOptions || []).forEach((opt) => {
      if (!opt) return;
      const id = String(opt.id ?? '').trim();
      if (!id) return;
      const header = opt.header != null ? String(opt.header) : '';

      const formattedFromHeader = header ? fmt(header) : '';
      const formattedFromId = fmt(id);

      let label = formattedFromHeader;
      if (!label || (label === header && formattedFromId && formattedFromId !== id)) {
        label = formattedFromId;
      }
      if (!label) label = formattedFromHeader || formattedFromId || header || id;

      map.set(id, label);
    });
    return map;
  }, [allColumnOptions, fmt]);

  const allRowColumnIds = useMemo(
    () => (allColumnOptions || [])
      .map((opt) => (opt?.id != null ? String(opt.id) : ''))
      .filter(Boolean),
    [allColumnOptions]
  );

  const handleRowColsChange = React.useCallback((idx, rawValue) => {
    const arr = Array.isArray(rawValue) ? rawValue : (rawValue == null ? [] : [rawValue]);
    const normalized = arr.map((val) => String(val));
    const hasSelectAll = normalized.includes(SELECT_ALL_ROW_COLS);
    const current = (() => {
      const row = pivotRows[idx] || {};
      if (Array.isArray(row.rowCols)) return row.rowCols.map((v) => String(v));
      return row.rowCol ? [String(row.rowCol)] : [];
    })();

    if (hasSelectAll) {
      const uniqueCurrent = Array.from(new Set(current));
      const total = allRowColumnIds.length;
      const allAlreadySelected = total > 0
        && uniqueCurrent.length === total
        && allRowColumnIds.every((id) => uniqueCurrent.includes(id));
      handlePivotChange(
        idx,
        'rowCols',
        allAlreadySelected ? [] : allRowColumnIds,
      );
      return;
    }

    const filtered = normalized.filter((val) => val !== SELECT_ALL_ROW_COLS);
    handlePivotChange(idx, 'rowCols', filtered);
  }, [allRowColumnIds, handlePivotChange, pivotRows]);

  return (
    <Box>           
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' },
          gap: 2,
        }}
      >
        {/* LEFT SIDEBAR */}
        <Paper
          sx={{
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.5, md: 2 },
            borderRadius: 4,
            border: '1px solid rgba(148, 163, 184, 0.18)',
            boxShadow: '0 18px 32px rgba(148, 163, 184, 0.16)',
            backgroundColor: '#ffffff',
            maxHeight: 600,
            overflow: 'auto',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#0f172a' }}>
            Tablolar
          </Typography>

          {sidebarGroups.length === 0 ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Tablo bulunamadı.
            </Typography>
          ) : (
            sidebarGroups.map((group) => {
              const isSelected = selectedGroupId === group.id;
              const isOpen = openGroupId === group.id;
              const filterLabel = summarizeGroupFilters(group.filtersApplied, group.isBaseGroup);

              return (
                <Box
                  key={group.id}
                  sx={{
                    mb: 1.25,
                    borderRadius: 2,
                    border: '1px solid rgba(148, 163, 184, 0.16)',
                    backgroundColor: 'rgba(248, 250, 252, 0.75)',
                    overflow: 'hidden',
                  }}
                >
                  <Button
                    fullWidth
                    size="small"
                    variant={isSelected ? 'contained' : 'text'}
                    onClick={() => {
                      setSelectedGroupId(group.id);
                      setOpenGroupId((prev) => (prev === group.id ? '' : group.id));
                      setRenderLimit(maxTablesPerSheet);
                      setExpanded(new Set());
                    }}
                    sx={{
                      justifyContent: 'space-between',
                      textTransform: 'none',
                      borderRadius: 0,
                      px: 1.25,
                      py: 0.75,
                      color: isSelected ? '#0f172a' : '#1f2937',
                      background: isSelected
                        ? 'linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%)'
                        : 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {group.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        ({group.tables.length})
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {isOpen ? '▲' : '▼'}
                    </Typography>
                  </Button>

                  {isOpen && (
                    <Box sx={{ px: 1, py: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {filterLabel}
                      </Typography>
                      <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.24)' }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {group.tables.map((it) => {
                      const isActive = expanded.has(it.id);
                      return (
                        <Button
                          key={it.id}
                          size="small"
                          variant={isActive ? 'contained' : 'text'}
                          onClick={async () => {
                            if (selectedGroupId !== group.id) setSelectedGroupId(group.id);
                            const target = tableCards.find((t) => t.id === it.id);
                            if (target && (target.rows === null || (Array.isArray(target.rows) && target.rows.length === 0))) {
                              try {
                                setImporting(true);
                                setImportText('Tablo yükleniyor…');
                                const loadedRows = await loadRowsForTable(it.id);
                                setTableRows(it.id, loadedRows);
                              } catch (e) {
                                console.warn('Tablo yüklenirken hata', e);
                                alert('Tablo satırları yüklenemedi.');
                              } finally {
                                setImporting(false);
                                setImportText('');
                              }
                            }
                            setExpanded((prev) => {
                              const next = new Set(prev);
                              if (next.has(it.id)) {
                                next.delete(it.id);
                              } else {
                                next.add(it.id);
                              }
                              return next;
                            });
                          }}
                          sx={{
                            justifyContent: 'flex-start',
                            width: '100%',
                            textTransform: 'none',
                            px: 1,
                            borderRadius: 1.5,
                            color: isActive ? '#0f172a' : '#1f2937',
                            backgroundColor: isActive ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(191, 219, 254, 0.45)',
                            },
                          }}
                        >
                          {it.title}
                        </Button>
                      );
                    })}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Paper>

        {/* ---------------- Pivot Alanı (TableCardPanel içinde) ---------------- */}
        <Paper sx={{ ...filterSurfaceSx, mb: 2 }}>
          <Stack spacing={1.5}>
            <Accordion
              disableGutters
              elevation={0}
              expanded={filterPanels.pivot}
              onChange={handleFilterPanelToggle('pivot')}
              sx={filterAccordionSx}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, py: 0.75 }}>
                <Typography variant="subtitle2">Pivot Tabloları</Typography>
                {renderSummaryChips([
                  { key: 'pivot-count', label: `Satır: ${pivotRows.length}` },
                  pivotRows.some((pv) => Array.isArray(pv.rowCols) && pv.rowCols.length) ? { key: 'pivot-row', label: 'Satır seçildi' } : null,
                  pivotRows.some((pv) => Array.isArray(pv.colCols) && pv.colCols.length) ? { key: 'pivot-col', label: 'Sütun seçildi' } : null,
                  pivotRows.some((pv) => pv.weightCol) ? { key: 'pivot-weight', label: 'Ağırlık' } : null,
                ])}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1, px: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.25}>
                  {pivotRows.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Henüz pivot satırı eklenmedi.
                    </Typography>
                  ) : (
                    pivotRows.map((pv, idx) => {
                      const selectedRowCols = Array.isArray(pv.rowCols)
                        ? pv.rowCols
                        : (pv.rowCol ? [pv.rowCol] : []);
                      const selectedColCols = Array.isArray(pv.colCols) ? pv.colCols : [];
                      const selectedColValue = selectedColCols[0] || '';
                      const selectedRowSet = new Set(selectedRowCols.map((val) => String(val)));
                      const totalRowOptions = allRowColumnIds.length;
                      const isAllRowsSelected = totalRowOptions > 0
                        && selectedRowSet.size === totalRowOptions
                        && allRowColumnIds.every((id) => selectedRowSet.has(id));
                      const isPartialRowsSelected = selectedRowSet.size > 0 && selectedRowSet.size < totalRowOptions;

                      return (
                        <Box key={idx} sx={filterRowCardSx}>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <FormControl size="small" sx={controlFieldSx}>
                              <InputLabel>Satır Kolonları</InputLabel>
                              <Select
                                multiple
                                label="Satır Kolonları"
                                value={selectedRowCols}
                                onChange={e => handleRowColsChange(idx, e.target.value)}
                                renderValue={(selected) => {
                                  const values = Array.isArray(selected) ? selected : [];
                                  if (!values.length) return '— boş (Toplam) —';
                                  if (allRowColumnIds.length > 0 && values.length === allRowColumnIds.length) {
                                    return 'Hepsi';
                                  }
                                  const labels = values.map((val) => {
                                    const key = String(val);
                                    return optionLabelById.get(key) ?? fmt(key) ?? key;
                                  });
                                  return labels.join(', ');
                                }}
                              >
                                <MenuItem value={SELECT_ALL_ROW_COLS}>
                                  <Checkbox
                                    checked={isAllRowsSelected}
                                    indeterminate={isPartialRowsSelected}
                                    size="small"
                                  />
                                  <ListItemText primary="Hepsini Seç" />
                                </MenuItem>
                                {allColumnOptions.map((opt) => {
                                  const optionKey = String(opt.id);
                                  const isSelected = selectedRowSet.has(optionKey);
                                  return (
                                    <MenuItem key={opt.id} value={optionKey}>
                                      <Checkbox checked={isSelected} size="small" />
                                      <ListItemText
                                        primary={
                                          optionLabelById.get(optionKey)
                                            ?? fmt(opt.header)
                                            ?? fmt(optionKey)
                                        }
                                      />
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>

                            <FormControl size="small" sx={controlFieldSx}>
                              <InputLabel>Sütun Kolonları</InputLabel>
                              <Select
                                label="Sütun Kolonları"
                                value={selectedColValue}
                                onChange={e => handlePivotChange(idx, 'colCols', e.target.value)}
                                displayEmpty
                                renderValue={(selected) => {
                                  const val = selected == null ? '' : String(selected);
                                  if (!val.trim()) return '— boş (Toplam) —';
                                  return optionLabelById.get(val) ?? fmt(val) ?? val;
                                }}
                              >
                                <MenuItem value="">
                                  <em>— boş (Toplam) —</em>
                                </MenuItem>
                                {allColumnOptions.map((opt) => {
                                  const optionKey = String(opt.id);
                                  return (
                                    <MenuItem key={opt.id} value={optionKey}>
                                      <ListItemText
                                        primary={
                                          optionLabelById.get(optionKey)
                                            ?? fmt(opt.header)
                                            ?? fmt(optionKey)
                                        }
                                      />
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>

                            <FormControl size="small" sx={controlFieldSx}>
                              <InputLabel>Ağırlık Kolonu</InputLabel>
                              <Select
                                label="Ağırlık Kolonu"
                                value={pv.weightCol}
                                onChange={e => handlePivotChange(idx, 'weightCol', e.target.value)}
                              >
                                <MenuItem value="">{'— boş —'}</MenuItem>
                                {allColumnOptions.map((opt) => {
                                  const optionKey = String(opt.id);
                                  return (
                                    <MenuItem key={opt.id} value={optionKey}>
                                      <ListItemText
                                        primary={
                                          optionLabelById.get(optionKey)
                                            ?? fmt(opt.header)
                                            ?? fmt(optionKey)
                                        }
                                      />
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>

                            <IconButton
                              size="small"
                              onClick={() => removePivotRow(idx)}
                              sx={{ color: 'error.main', ml: 'auto' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Box>
                      );
                    })
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addPivotRow}>
                      Pivot Satırı Ekle
                    </Button>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion
              disableGutters
              elevation={0}
              expanded={filterPanels.multi}
              onChange={handleFilterPanelToggle('multi')}
              sx={filterAccordionSx}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, py: 0.75 }}>
                <Typography variant="subtitle2">Multi Soru Tabloları</Typography>
                {renderSummaryChips([
                  { key: 'multi-count', label: `Satır: ${multiQuestionRows.length}` },
                  multiQuestionRows.some((mq) => Array.isArray(mq.aggregation) ? mq.aggregation.length : mq.aggregation) ? { key: 'multi-agg', label: 'Likert seçildi' } : null,
                ])}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1, px: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.25}>
                  {multiQuestionRows.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Multi soru satırı eklemediniz.
                    </Typography>
                  ) : (
                    multiQuestionRows.map((mq, idx) => {
                      const selectedQuestions = Array.isArray(mq.questions)
                        ? mq.questions.map((val) => String(val))
                        : [];
                      const likertEligible = areAllLikertQuestions(selectedQuestions);
                      const aggregationValue = Array.isArray(mq.aggregation)
                        ? mq.aggregation
                        : (mq.aggregation ? [String(mq.aggregation)] : []);

                      return (
                        <Box key={`multi-${idx}`} sx={filterRowCardSx}>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <FormControl size="small" sx={controlFieldSx}>
                              <InputLabel>Multi Sorular</InputLabel>
                              <Select
                                multiple
                                label="Multi Sorular"
                                value={selectedQuestions}
                                onChange={(e) => handleMultiQuestionChange(idx, 'questions', e.target.value)}
                                renderValue={(selected) => {
                                  const values = Array.isArray(selected) ? selected : [];
                                  if (!values.length) return '— soru seçin —';
                                  const labels = values.map((val) => {
                                    const key = String(val);
                                    return optionLabelById.get(key) ?? fmt(key) ?? key;
                                  });
                                  return labels.join(', ');
                                }}
                              >
                                {allColumnOptions.map((opt) => {
                                  const optionKey = String(opt.id);
                                  const isSelected = selectedQuestions.includes(optionKey);
                                  return (
                                    <MenuItem key={`mq-${opt.id}`} value={optionKey}>
                                      <Checkbox checked={isSelected} size="small" />
                                      <ListItemText
                                        primary={
                                          optionLabelById.get(optionKey)
                                            ?? fmt(opt.header)
                                            ?? fmt(optionKey)
                                        }
                                      />
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>

                            {likertEligible && (
                              <FormControl size="small" sx={controlFieldSx}>
                                <InputLabel>Likert Hesaplama</InputLabel>
                                <Select
                                  multiple
                                  label="Likert Hesaplama"
                                  value={aggregationValue}
                                  onChange={(e) => handleMultiQuestionChange(idx, 'aggregation', e.target.value)}
                                  renderValue={(selected) => {
                                    const values = Array.isArray(selected) ? selected : [];
                                    if (!values.length) return '— seçim yapın —';
                                    return values
                                      .map((val) => LIKERT_CALC_LABEL_MAP.get(val) || val)
                                      .join(', ');
                                  }}
                                >
                                  {LIKERT_CALC_OPTIONS.map((opt) => {
                                    const isSelected = aggregationValue.includes(opt.value);
                                    return (
                                      <MenuItem key={`mq-agg-${opt.value}`} value={opt.value}>
                                        <Checkbox checked={isSelected} size="small" />
                                        <ListItemText primary={opt.label} />
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            )}

                            <FormControl size="small" sx={controlFieldSx}>
                              <InputLabel>Ağırlık Kolonu</InputLabel>
                              <Select
                                label="Ağırlık Kolonu"
                                value={mq.weightCol}
                                onChange={(e) => handleMultiQuestionChange(idx, 'weightCol', e.target.value)}
                              >
                                <MenuItem value="">{'— boş —'}</MenuItem>
                                {allColumnOptions.map((opt) => {
                                  const optionKey = String(opt.id);
                                  return (
                                    <MenuItem key={`mq-weight-${opt.id}`} value={optionKey}>
                                      <ListItemText
                                        primary={
                                          optionLabelById.get(optionKey)
                                            ?? fmt(opt.header)
                                            ?? fmt(optionKey)
                                        }
                                      />
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>

                            <IconButton
                              size="small"
                              onClick={() => removeMultiQuestionRow(idx)}
                              sx={{ color: 'error.main', ml: 'auto' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Box>
                      );
                    })
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addMultiQuestionRow}>
                      Multi Soru Satırı Ekle
                    </Button>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion
              disableGutters
              elevation={0}
              expanded={filterPanels.undupe}
              onChange={handleFilterPanelToggle('undupe')}
              sx={filterAccordionSx}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, py: 0.75 }}>
                <Typography variant="subtitle2">Undupe Soruları</Typography>
                {renderSummaryChips([
                  { key: 'undupe-count', label: `Satır: ${undupeQuestions.length}` },
                ])}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1, px: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.25}>
                  {undupeQuestions.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Henüz undupe sorusu eklenmedi. Temel soruyu seçerek başlayabilirsiniz.
                    </Typography>
                  ) : (
                    undupeQuestions.map((uq) => {
                      const baseOptions = getQuestionOptions(uq.baseQuestionId);
                      const hasBaseOptions = Array.isArray(baseOptions) && baseOptions.length > 0;
                      const hiddenSet = new Set((uq.hiddenOptionValues || []).map((val) => String(val)));
                      const merges = Array.isArray(uq.merges) ? uq.merges : [];

                      return (
                        <Box
                          key={uq.id}
                          sx={filterRowCardSx}
                        >
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="flex-end"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <FormControl size="small" sx={controlFieldSx}>
                              <InputLabel>Temel Soru</InputLabel>
                              <Select
                                label="Temel Soru"
                                value={uq.baseQuestionId || ''}
                                onChange={(e) => handleUndupeFieldChange(uq.id, 'baseQuestionId', e.target.value)}
                              >
                                <MenuItem value="">
                                  <em>Seçiniz</em>
                                </MenuItem>
                                {undupeBaseQuestionOptions.map((opt) => {
                                  const optId = String(opt.id);
                                  const label = optionLabelById.get(optId) ?? fmt(opt.header) ?? opt.header ?? optId;
                                  return (
                                    <MenuItem key={opt.id} value={optId}>
                                      {label}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                            <TextField
                              size="small"
                              label="Yeni Soru Başlığı"
                              sx={{ flex: '1 1 220px', minWidth: 180 }}
                              value={uq.newQuestionLabel || ''}
                              onChange={(e) => handleUndupeFieldChange(uq.id, 'newQuestionLabel', e.target.value)}
                              placeholder={uq.baseQuestionId ? `${fmt(uq.baseQuestionId) || uq.baseQuestionId} (Undupe)` : ''}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removeUndupeQuestion(uq.id)}
                              sx={{ color: 'error.main', ml: 'auto' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>

                          {(merges || []).map((merge) => {
                            const mergeValues = Array.isArray(merge.mergeOptionValues) ? merge.mergeOptionValues : [];
                            const mergeSet = new Set(mergeValues.map((val) => String(val)));
                            const placeholderLabel = mergeValues.length
                              ? mergeValues
                                  .map((val) => getOptionLabel(uq.baseQuestionId, val))
                                  .filter(Boolean)
                                  .join(' + ')
                              : '';
                            return (
                              <Stack
                                key={merge.id}
                                direction="row"
                                spacing={0.75}
                                alignItems="flex-end"
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ mt: 0.75 }}
                              >
                                <FormControl size="small" sx={{ ...controlFieldSx, minWidth: 200 }}>
                                  <InputLabel>Birleştirilecek Cevaplar</InputLabel>
                                  <Select
                                    multiple
                                    label="Birleştirilecek Cevaplar"
                                    value={mergeValues}
                                    onChange={(e) => handleUndupeMergeChange(uq.id, merge.id, 'mergeOptionValues', e.target.value)}
                                    disabled={!uq.baseQuestionId || !hasBaseOptions}
                                    renderValue={(selected) => {
                                      const arr = Array.isArray(selected) ? selected : [];
                                      if (!arr.length) return 'Seçiniz';
                                      return arr
                                        .map((val) => getOptionLabel(uq.baseQuestionId, val))
                                        .filter(Boolean)
                                        .join(', ');
                                    }}
                                  >
                                    {hasBaseOptions ? (
                                      baseOptions.map((opt) => {
                                        const optVal = String(opt.value);
                                        return (
                                          <MenuItem key={`${uq.id}_merge_${merge.id}_${optVal}`} value={optVal}>
                                            <Checkbox size="small" checked={mergeSet.has(optVal)} />
                                            <ListItemText primary={opt.label} />
                                          </MenuItem>
                                        );
                                      })
                                    ) : (
                                      <MenuItem value="" disabled>
                                        <ListItemText primary="Bu soru için cevap bulunamadı" />
                                      </MenuItem>
                                    )}
                                  </Select>
                                </FormControl>
                                <TextField
                                  size="small"
                                  label="Yeni Cevap Başlığı"
                                  sx={{ flex: '1 1 200px', minWidth: 180 }}
                                  value={merge.newOptionLabel || ''}
                                  placeholder={placeholderLabel || 'Örn. Birleştirilmiş Cevap'}
                                  onChange={(e) => handleUndupeMergeChange(uq.id, merge.id, 'newOptionLabel', e.target.value)}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => removeUndupeMerge(uq.id, merge.id)}
                                  sx={{ color: 'error.main', ml: 'auto' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            );
                          })}

                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
                            <Button
                              variant="text"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => addUndupeMerge(uq.id)}
                              disabled={!uq.baseQuestionId}
                            >
                              Birleşik Cevap Ekle
                            </Button>

                            <FormControl size="small" sx={{ ...controlFieldSx, minWidth: 200 }}>
                              <InputLabel>Gizlenecek Cevaplar</InputLabel>
                              <Select
                                multiple
                                label="Gizlenecek Cevaplar"
                                value={uq.hiddenOptionValues || []}
                                onChange={(e) => handleUndupeFieldChange(uq.id, 'hiddenOptionValues', e.target.value)}
                                disabled={!uq.baseQuestionId || !hasBaseOptions}
                                renderValue={(selected) => {
                                  const arr = Array.isArray(selected) ? selected : [];
                                  if (!arr.length) return 'Gösterilecek';
                                  return arr
                                    .map((val) => getOptionLabel(uq.baseQuestionId, val))
                                    .filter(Boolean)
                                    .join(', ');
                                }}
                              >
                                {hasBaseOptions ? (
                                  baseOptions.map((opt) => {
                                    const optVal = String(opt.value);
                                    return (
                                      <MenuItem key={`${uq.id}_hidden_${optVal}`} value={optVal}>
                                        <Checkbox size="small" checked={hiddenSet.has(optVal)} />
                                        <ListItemText primary={opt.label} />
                                      </MenuItem>
                                    );
                                  })
                                ) : (
                                  <MenuItem value="" disabled>
                                    <ListItemText primary="Bu soru için cevap bulunamadı" />
                                  </MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          </Stack>
                        </Box>
                      );
                    })
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addUndupeQuestion}
                    >
                      Undupe Question Ekle
                    </Button>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="contained" size="small" onClick={runPivotAndAddTables}>
                  Seçimleri Çalıştır ve Tablo Ekle
                </Button>
                <Button variant="outlined" size="small" onClick={handleOpenAiDialog}>
                  AI ile Tablo Oluştur
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  disabled={importing}
                >
                  Tablo Ekle
                </Button>
                <Button variant="outlined" size="small" onClick={handlePasteCSV} disabled={importing}>
                  Panodan Yapıştır
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteAll}
                  disabled={importing}
                >
                  Tüm Tabloları Sil
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Box>
      <Dialog open={aiDialogOpen} onClose={handleCloseAiDialog} fullWidth maxWidth="sm">
        <DialogTitle>AI ile Tablo Oluştur</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            multiline
            minRows={3}
            label="İstek"
            placeholder="Örn. 18-24 yaş arası kullanıcıların memnuniyet dağılımı"
            fullWidth
            value={aiPrompt}
            onChange={handleAiPromptChange}
            helperText={aiPromptError || 'İhtiyacınızı birkaç cümleyle açıklayın.'}
            error={Boolean(aiPromptError)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAiDialog}>İptal</Button>
          <Button variant="contained" onClick={handleAiTableCreation} disabled={persisting}>
            Oluştur
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop open={importing || persisting} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Box sx={{ minWidth: 320 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={28} />
            <Typography variant="body1">
              {persisting ? `Firebase’e kaydediliyor… ${persistText}` : `İşlem devam ediyor… ${importText}`}
            </Typography>
          </Box>
          <LinearProgress />
        </Box>
      </Backdrop>

      <input
        type="file"
        accept=".xlsx,.xls,.csv,.txt"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => handleAddTableFromFile(e.target.files?.[0])}
      />
      
        {/* RIGHT: Cards */}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            minWidth: 0,
          }}
        >
          {selectedTables.length === 0 ? (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Bir tablo seçin.
            </Typography>
          ) : (
            <>
              {selectedTables.map(tbl => (
                <TableCard
                  key={tbl.id}
                  title={tbl.sheet ? `${tbl.sheet} / ${tbl.title}` : tbl.title}
                  rows={tbl.rows}
                  columns={tbl.columns}
                  significance={tbl.significance}
                  lettersMap={tbl.lettersMap}
                  comparisonGroups={tbl.comparisonGroups}
                  onRename={() => handleRename(tbl.id)}
                  onDelete={() => handleDelete(tbl.id)}
                  collapsed={!isExpanded(tbl.id)}
                  onToggle={() => {
                    if (tbl.rows === null && (tbl._metaRowCount ?? 0) > 0) {
                      alert('Veriyi yüklemek için soldaki listeden tabloya tıklayın.');
                      return;
                    }
                    toggleExpand(tbl.id);
                  }}
                  meta={{
                    sheet: tbl.sheet,
                    rowCount: (tbl._metaRowCount ?? tbl.rows?.length ?? 0),
                    colCount: (tbl.columns?.length || 0)
                  }}
                  colorThemes={colorThemes}
                  defaultColorThemeId={defaultColorThemeId}
                />
              ))}
            </>
          )}
        </Box>
    
    </Box>
  );
}

// Convenience function to load latest tables from Firestore (after API triggers a new run)
export async function loadTablesFromLatestRun(db, projectID) {
  const { tables } = await readLatestReportingRun(db, projectID);
  return tables.map((t) => tableDocToCard(t)).filter(Boolean);
}
